import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NavLink, useLocation, useNavigate, useParams} from "react-router-dom";
import {isPossiblePhoneNumber} from "react-phone-number-input";
import LivenessDetector, {DEFAULT_LIVENESS_TEXTS} from "mobility-f57";
import "mobility-f57/styles.css";
import Header from "../../ThemeComponents/Header";
import Footer from "../../ThemeComponents/Footer";
import ReservationEmailField from "../../ThemeComponents/ReservationPage/Fields/ReservationEmailField";
import PasswordField from "../../ThemeComponents/ReservationPage/Fields/PasswordField";
import {isValidEmail} from "../../config/toolKit";
import {LIVENESS_WS_URL} from "../../config/network";
import {
    appendPublicWrapperBaseToSearch,
    resolvePublicWrapperBase
} from "../../config/publicWrapperBase";
import {
    generateDemoReservationEmail,
    generateDemoReservationPhone,
    isDemoReservationLocation as isDemoReservationLocationId
} from "../../config/demoReservation";
import {detailVehicleAPI} from "../../network/publicCalls/GETVehicleDetail";
import {searchUserAPI} from "../../network/publicCalls/POSTSearchUser";
import {validateEmailAPI} from "../../network/publicCalls/GETValidateEmail";
import {registerClientAPI} from "../../network/publicCalls/POSTRegisterClient";
import {postalCodeLookupAPI} from "../../network/publicCalls/GETPostalCodeLookup";
import {idsfTransactionGroupStartAPI} from "../../network/publicCalls/POSTIdsfTransactionGroupStart";
import {idsfTransactionGroupAppendAPI} from "../../network/publicCalls/PATCHIdsfTransactionGroupAppend";
import {faceIdAPI} from "../../network/publicCalls/POSTFaceId";
import {getCustomersAPI} from "../../network/loggedInCalls/GETCustomers";
import {getContractAPI} from "../../network/publicCalls/GETContract";
import {signContractAPI} from "../../network/loggedInCalls/PATCHSignContract";
import {uploadUserDocumentAPI} from "../../network/loggedInCalls/POSTUploadUserDocument";
import DocumentCapturePanel from "../../ThemeComponents/ReservationPage/DocumentCapturePanel";
import useBlockBrowserBack from "../../ThemeComponents/ReservationPage/useBlockBrowserBack";
import useDocumentCaptureSession, {
    DOCUMENT_CAPTURE_PHASE_DELIVERED,
    DOCUMENT_CAPTURE_PHASE_IDLE,
} from "../../ThemeComponents/ReservationPage/useDocumentCaptureSession";
import {
    extractDocumentCaptureRecognitionFields,
    findDocumentCaptureImage,
    toDocumentCaptureImageDataUrl,
} from "../../config/documentCapture";
import {resolveWhiteLabelThemeId} from "../../config/whiteLabelThemes";
import {resolveWhiteLabelSubsidiaryId} from "../../config/subsidiaryId";
import {forceScrollToTop} from "../../config/scrollToTop";
import {sendEvent, setUserId} from "../../config/analytics";
import * as countryCodesList from "country-codes-list";
import demoLogo from "../../assets/images/demo.png";
import Devider from "../../ThemeComponents/Devider";
import ensingContract from "../../assets/whitelabel/ensing/ensing-contract.pdf";
import ensingContract2 from "../../assets/whitelabel/ensing/ensing-contract-2.pdf";

const STEP_ACCESS = "access";
const STEP_ACCOUNT = "account";
const STEP_DOCUMENT_INTRO = "document_intro";
const STEP_DOCUMENT_CAPTURE = "document_capture";
const STEP_DOCUMENT_DATA = "document_data";
const STEP_SELFIE = "selfie";
const STEP_ADDRESS = "address";
const STEP_CONTRACT = "contract";
const REGISTRATION_STEP_VIEW_EVENTS = {
    [STEP_ACCESS]: "reserve_register_access_view",
    [STEP_ACCOUNT]: "reserve_register_account_view",
    [STEP_DOCUMENT_INTRO]: "reserve_register_document_intro_view",
    [STEP_DOCUMENT_CAPTURE]: "reserve_register_document_capture_view",
    [STEP_DOCUMENT_DATA]: "reserve_register_document_data_view",
    [STEP_SELFIE]: "reserve_register_selfie_view",
    [STEP_ADDRESS]: "reserve_register_address_view",
    [STEP_CONTRACT]: "reserve_register_contract_view",
};
const IDSF_GROUP_QUERY_PARAM = "idsf_group_id";
const LEGACY_IDSF_GROUP_QUERY_PARAM = "group_id";
const IDSF_GROUP_STORAGE_PREFIX = "kav_reservation_idsf_group_id";
const DOCUMENT_IMAGE_MAX_WIDTH_LANDSCAPE = 1280;
const DOCUMENT_IMAGE_MAX_HEIGHT_LANDSCAPE = 720;
const DOCUMENT_IMAGE_MAX_WIDTH_PORTRAIT = 720;
const DOCUMENT_IMAGE_MAX_HEIGHT_PORTRAIT = 1280;
const DOCUMENT_IMAGE_OUTPUT_MIME_TYPE = "image/jpeg";
const DOCUMENT_IMAGE_OUTPUT_QUALITY = 0.82;
const DEMO_DEFAULT_ADDRESS_ZIP_CODE = "1101AK";
const DEMO_DEFAULT_ADDRESS_HOUSE_NUMBER = "17";
const WHITE_LABEL_CONTRACT_FILES = {
    ensing: [
        {
            type: "contract",
            url: ensingContract,
            checked: true,
        },
        {
            type: "terms",
            url: ensingContract2,
            checked: true,
        },
    ],
};
const EMPTY_DOCUMENT_DATA = {
    firstName: "",
    surname: "",
    issueCountry: "",
    licenseNumber: "",
    expirationDate: "",
    birthDate: "",
    categoryB: false,
    birthCountry: "",
    nationality: "",
};
const DOCUMENT_COUNTRY_FIELD_META = {
    issueCountry: {
        label: "Land van afgifte",
        modalTitle: "Kies land van afgifte",
    },
    birthCountry: {
        label: "Geboorteland",
        modalTitle: "Kies geboorteland",
    },
    nationality: {
        label: "Nationaliteit",
        modalTitle: "Kies nationaliteit",
    },
};
const COUNTRY_OPTIONS = countryCodesList
    .all()
    .map((country) => ({
        countryCode: String(country.countryCode || "").toUpperCase(),
        countryCallingCode: String(country.countryCallingCode || "").replace(/[^\d]/g, ""),
        countryNameEn: String(country.countryNameEn || ""),
        countryNameLocal: String(country.countryNameLocal || ""),
        flag: String(country.flag || ""),
    }))
    .sort((a, b) => a.countryNameEn.localeCompare(b.countryNameEn));
const COUNTRY_OPTIONS_BY_ALPHA2 = COUNTRY_OPTIONS.reduce((result, country) => {
    result[country.countryCode] = country;
    return result;
}, {});
const COUNTRY_OPTIONS_BY_CALLING_CODE = COUNTRY_OPTIONS.reduce((result, country) => {
    if (!country.countryCallingCode || result[country.countryCallingCode]) return result;
    result[country.countryCallingCode] = country;
    return result;
}, {});
const COUNTRY_CALLING_CODES_DESC = Object.keys(COUNTRY_OPTIONS_BY_CALLING_CODE)
    .sort((left, right) => right.length - left.length);
const filterCountryOptions = (searchValue = "") => {
    const searchTerm = String(searchValue || "").trim().toLowerCase();
    if (!searchTerm) return COUNTRY_OPTIONS;

    return COUNTRY_OPTIONS.filter((country) => (
        country.countryNameEn.toLowerCase().includes(searchTerm) ||
        country.countryNameLocal.toLowerCase().includes(searchTerm) ||
        country.countryCode.toLowerCase().includes(searchTerm) ||
        country.countryCallingCode.toLowerCase().includes(searchTerm)
    ));
};
const sanitizePhoneDigits = (value = "") => String(value || "").replace(/[^\d]/g, "");
const splitInternationalPhoneNumber = (value = "") => {
    const digits = sanitizePhoneDigits(value);
    const fallbackCountry = COUNTRY_OPTIONS_BY_ALPHA2.NL || COUNTRY_OPTIONS[0] || null;

    if (!digits) {
        return {
            countryAlpha2: fallbackCountry?.countryCode || "NL",
            countryCallingCode: fallbackCountry?.countryCallingCode || "31",
            nationalNumber: "",
        };
    }

    const matchedCallingCode = COUNTRY_CALLING_CODES_DESC.find((code) => digits.startsWith(code)) || (fallbackCountry?.countryCallingCode || "31");
    const matchedCountry = COUNTRY_OPTIONS_BY_CALLING_CODE[matchedCallingCode] || fallbackCountry;

    return {
        countryAlpha2: matchedCountry?.countryCode || "NL",
        countryCallingCode: matchedCallingCode,
        nationalNumber: digits.slice(matchedCallingCode.length),
    };
};

const isMobileViewport = () => {
    if (typeof window === "undefined") return false;
    const narrowScreen = window.matchMedia("(max-width: 900px)").matches;
    const touchDevice = window.matchMedia("(pointer: coarse)").matches;
    const mobileUa = /Android|iPhone|iPad|iPod|Mobile/i.test(window.navigator.userAgent || "");
    return narrowScreen || touchDevice || mobileUa;
};

const isStrongPassword = (password) => /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(password || "");
const normalizeZipCode = (value = "") => String(value).replace(/\s+/g, "").toUpperCase();
const normalizeCountryAlpha2 = (value = "") => {
    const normalized = String(value || "").trim().toUpperCase();
    if (!normalized) return "";
    if (COUNTRY_OPTIONS_BY_ALPHA2[normalized]) return normalized;
    if (normalized === "NEDERLAND" || normalized === "NETHERLANDS" || normalized === "HOLLAND" || normalized === "NLD") {
        return "NL";
    }

    const byName = COUNTRY_OPTIONS.find((country) => (
        country.countryNameEn.toUpperCase() === normalized ||
        country.countryNameLocal.toUpperCase() === normalized
    ));
    if (byName) return byName.countryCode;

    if (normalized.length === 2) return normalized;
    return "";
};
const extractBase64ImageData = (value = "") => {
    const normalized = String(value || "").trim();
    if (!normalized) return null;

    const dataUrlMatch = normalized.match(/^data:([^;]+);base64,(.+)$/);
    if (dataUrlMatch) {
        return {
            mimeType: dataUrlMatch[1],
            data: dataUrlMatch[2],
        };
    }

    return {
        mimeType: "image/jpeg",
        data: normalized,
    };
};
const dataUrlToBlob = async (dataUrl) => {
    const response = await fetch(dataUrl);
    return await response.blob();
};
const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Bestand kon niet worden gelezen."));
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsDataURL(blob);
});
const loadImageElementFromBlob = (blob) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Afbeelding kon niet worden gedecodeerd."));
    };
    image.src = objectUrl;
});
const getDocumentResizeDimensions = ({width, height}) => {
    const isLandscape = width >= height;
    const maxWidth = isLandscape ? DOCUMENT_IMAGE_MAX_WIDTH_LANDSCAPE : DOCUMENT_IMAGE_MAX_WIDTH_PORTRAIT;
    const maxHeight = isLandscape ? DOCUMENT_IMAGE_MAX_HEIGHT_LANDSCAPE : DOCUMENT_IMAGE_MAX_HEIGHT_PORTRAIT;
    const scale = Math.min(maxWidth / width, maxHeight / height, 1);
    return {
        targetWidth: Math.max(1, Math.round(width * scale)),
        targetHeight: Math.max(1, Math.round(height * scale)),
    };
};
const compressDocumentPhotoToDataUrl = async (file) => {
    if (!(file instanceof Blob)) {
        throw new Error("Ongeldig afbeeldingsbestand.");
    }
    if (typeof document === "undefined") {
        return await readBlobAsDataUrl(file);
    }

    let source = null;
    let sourceHasClose = false;

    try {
        if (typeof window !== "undefined" && typeof window.createImageBitmap === "function") {
            try {
                source = await window.createImageBitmap(file);
                sourceHasClose = typeof source.close === "function";
            } catch (_error) {
                source = null;
            }
        }
        if (!source) {
            source = await loadImageElementFromBlob(file);
        }

        const sourceWidth = Number(source.width || source.naturalWidth || 0);
        const sourceHeight = Number(source.height || source.naturalHeight || 0);
        if (!sourceWidth || !sourceHeight) {
            throw new Error("Afbeelding heeft een ongeldig formaat.");
        }

        const {targetWidth, targetHeight} = getDocumentResizeDimensions({
            width: sourceWidth,
            height: sourceHeight,
        });
        const sourceSizeKb = Math.round((Number(file.size || 0) / 1024) * 10) / 10;
        console.info("[doc-resize] start", {
            sourceWidth,
            sourceHeight,
            targetWidth,
            targetHeight,
            sourceSizeKb,
            outputMimeType: DOCUMENT_IMAGE_OUTPUT_MIME_TYPE,
            outputQuality: DOCUMENT_IMAGE_OUTPUT_QUALITY,
        });

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const context = canvas.getContext("2d");
        if (!context) {
            throw new Error("Afbeelding kon niet worden verwerkt.");
        }

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        context.drawImage(source, 0, 0, targetWidth, targetHeight);

        const compressedBlob = await new Promise((resolve, reject) => {
            canvas.toBlob((nextBlob) => {
                if (!nextBlob) {
                    reject(new Error("Afbeelding kon niet worden gecomprimeerd."));
                    return;
                }
                resolve(nextBlob);
            }, DOCUMENT_IMAGE_OUTPUT_MIME_TYPE, DOCUMENT_IMAGE_OUTPUT_QUALITY);
        });
        const compressedSizeKb = Math.round((Number(compressedBlob.size || 0) / 1024) * 10) / 10;
        console.info("[doc-resize] done", {
            sourceWidth,
            sourceHeight,
            targetWidth,
            targetHeight,
            sourceSizeKb,
            compressedSizeKb,
            savedKb: Math.round((sourceSizeKb - compressedSizeKb) * 10) / 10,
        });

        return await readBlobAsDataUrl(compressedBlob);
    } finally {
        if (sourceHasClose && source && typeof source.close === "function") {
            source.close();
        }
    }
};
const normalizeDocumentCountry = (value = "") => {
    const normalized = normalizeCountryAlpha2(value);
    if (!normalized) return "NL";
    return normalized;
};
const normalizeDocumentDate = (value = "") => {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

    const match = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (!match) return normalized;

    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};
const isValidOcrValue = (value) => {
    const normalized = String(value || "").trim();
    return Boolean(normalized) && normalized.toLowerCase() !== "n/a";
};
const buildDocumentDataFromOcrFields = (fields) => {
    const countryValue = normalizeCountryAlpha2(fields?.country);
    const fullNameValue = isValidOcrValue(fields?.full_name)
        ? String(fields.full_name).trim()
        : "";
    const fullNameParts = fullNameValue ? fullNameValue.split(/\s+/).filter(Boolean) : [];
    const firstNameFromFull = fullNameParts.length ? fullNameParts[0] : "";
    const surnameFromFull = fullNameParts.length > 1 ? fullNameParts.slice(1).join(" ") : "";
    const categoryList = Array.isArray(fields?.drivers_license_categories)
        ? fields.drivers_license_categories.map((item) => String(item || "").toUpperCase())
        : [];

    return {
        firstName: isValidOcrValue(fields?.first_name) ? String(fields.first_name).trim() : firstNameFromFull,
        surname: isValidOcrValue(fields?.last_name) ? String(fields.last_name).trim() : surnameFromFull,
        issueCountry: countryValue,
        licenseNumber: isValidOcrValue(fields?.number) ? String(fields.number).trim() : "",
        expirationDate: isValidOcrValue(fields?.expiration_date)
            ? formatDocumentDateForInput(fields.expiration_date)
            : "",
        birthDate: isValidOcrValue(fields?.date_of_birth)
            ? formatDocumentDateForInput(fields.date_of_birth)
            : "",
        categoryB: categoryList.includes("B"),
        birthCountry: normalizeCountryAlpha2(fields?.country_of_birth) || countryValue,
        nationality: normalizeCountryAlpha2(fields?.nationality) || countryValue,
    };
};
// The manual review step is only shown when OCR left a gap; a fully recognized
// document goes straight on to the next step.
const isDocumentDataComplete = (data) => {
    const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
    return Boolean(
        String(data?.firstName || "").trim()
        && String(data?.surname || "").trim()
        && data?.issueCountry
        && String(data?.licenseNumber || "").trim()
        && isIsoDate(data?.expirationDate)
        && isIsoDate(data?.birthDate)
        && data?.categoryB
        && data?.birthCountry
        && data?.nationality
    );
};
const formatDocumentDateForInput = (value = "") => {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

    const dateParts = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dateParts) {
        const [, day, month, year] = dateParts;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    return "";
};
const normalizeIdsfGroupId = (value = "") => String(value || "").trim();
const getIdsfGroupIdFromQuery = (queryParams) => normalizeIdsfGroupId(
    queryParams.get(IDSF_GROUP_QUERY_PARAM) || queryParams.get(LEGACY_IDSF_GROUP_QUERY_PARAM) || ""
);
const getIdsfGroupIdFromStartResponse = (response) => normalizeIdsfGroupId(
    typeof response === "string"
        ? response
        : (
            response?.idsf_group_id ||
            response?.group_id ||
            response?.id ||
            response?.payload?.idsf_group_id ||
            response?.payload?.group_id ||
            response?.payload?.id ||
            ""
        )
);
const getIdsfGroupStorageKey = ({vehicleId, locationId}) => (
    `${IDSF_GROUP_STORAGE_PREFIX}:${vehicleId || "unknown"}:${locationId || "unknown"}`
);

const ReservationRegisterPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {vehicleId, locationId} = useParams();
    const isDemoReservationLocation = isDemoReservationLocationId(locationId);

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const reservationContext = useMemo(() => ({
        from: queryParams.get("from") || "",
        to: queryParams.get("to") || "",
        expectedKm: queryParams.get("expectedKm") || "",
        options: queryParams.get("options") || "",
        products: queryParams.get("products") || "",
        email: queryParams.get("email") || "",
    }), [queryParams]);
    const resolvedReservationEmail = useMemo(() => {
        const queryEmail = String(reservationContext.email || "").trim();
        if (queryEmail) return queryEmail;
        if (isDemoReservationLocation) return generateDemoReservationEmail();
        return "";
    }, [reservationContext.email, isDemoReservationLocation]);
    const resolvedReservationPhone = useMemo(() => {
        if (!isDemoReservationLocation) return "";
        return generateDemoReservationPhone();
    }, [isDemoReservationLocation]);
    const publicWrapperBase = useMemo(
        () => resolvePublicWrapperBase(queryParams),
        [queryParams]
    );
    const idsfGroupStorageKey = useMemo(
        () => getIdsfGroupStorageKey({vehicleId, locationId}),
        [vehicleId, locationId]
    );
    const [idsfGroupId, setIdsfGroupId] = useState(() => {
        const queryGroupId = getIdsfGroupIdFromQuery(queryParams);
        if (queryGroupId) return queryGroupId;

        if (typeof window === "undefined") return "";
        try {
            return normalizeIdsfGroupId(window.localStorage.getItem(idsfGroupStorageKey));
        } catch (_error) {
            return "";
        }
    });

    const [isMobileFlow, setIsMobileFlow] = useState(isMobileViewport());
    // Consent is collected on the confirm page; the access step only remains as a fallback for direct visits.
    const consentConfirmedUpfront = queryParams.get("consent") === "1";
    const [step, setStep] = useState(consentConfirmedUpfront ? STEP_ACCOUNT : STEP_ACCESS);

    const [vehicleInfo, setVehicleInfo] = useState(null);
    const [isVehicleLoading, setIsVehicleLoading] = useState(true);
    const [vehicleLoadError, setVehicleLoadError] = useState("");

    const [showConsentModal, setShowConsentModal] = useState(false);
    const [consentIdChecked, setConsentIdChecked] = useState(false);
    const [consentPrivacyChecked, setConsentPrivacyChecked] = useState(false);

    const [accountData, setAccountData] = useState({
        phoneNumber: "",
        email: resolvedReservationEmail,
        sendPasswordBySms: false,
        password: "",
        repeatPassword: "",
    });
    const [phoneInputData, setPhoneInputData] = useState(() => splitInternationalPhoneNumber(resolvedReservationPhone));
    const [isPhoneAutoGenerated, setIsPhoneAutoGenerated] = useState(Boolean(resolvedReservationPhone));
    const [requiresManualPhoneInput, setRequiresManualPhoneInput] = useState(false);
    const [phoneCountrySelectorState, setPhoneCountrySelectorState] = useState({
        isOpen: false,
        searchTerm: "",
    });

    const [showAccountConfirmModal, setShowAccountConfirmModal] = useState(false);
    const [showCustomerExistsModal, setShowCustomerExistsModal] = useState(false);
    const [showAnotherSubsidiaryModal, setShowAnotherSubsidiaryModal] = useState(false);
    const [showEmailNotValidModal, setShowEmailNotValidModal] = useState(false);
    const [isEmailValidationUnavailable, setIsEmailValidationUnavailable] = useState(false);

    const [isAccountCheckLoading, setIsAccountCheckLoading] = useState(false);
    const [accountCheckError, setAccountCheckError] = useState("");

    const [showDocumentConfirmModal, setShowDocumentConfirmModal] = useState(false);
    const [agreedSelfieStorage, setAgreedSelfieStorage] = useState(true);
    const [isRegistrationLoading, setIsRegistrationLoading] = useState(false);
    const [registrationError, setRegistrationError] = useState("");

    const [documentData, setDocumentData] = useState(EMPTY_DOCUMENT_DATA);
    const [countrySelectorState, setCountrySelectorState] = useState({
        isOpen: false,
        field: "",
        searchTerm: "",
    });
    const [addressCountrySelectorState, setAddressCountrySelectorState] = useState({
        isOpen: false,
        addressType: "",
        searchTerm: "",
    });
    const [documentOcrError, setDocumentOcrError] = useState("");
    const [isDocumentCaptureResultProcessing, setIsDocumentCaptureResultProcessing] = useState(false);

    const [frontLicensePhoto, setFrontLicensePhoto] = useState("");
    const [selfiePhoto, setSelfiePhoto] = useState("");
    const [isLivenessActive, setIsLivenessActive] = useState(false);
    const [canUseFallbackSelfieCapture, setCanUseFallbackSelfieCapture] = useState(false);
    const [isSelfieCaptureProcessing, setIsSelfieCaptureProcessing] = useState(false);
    const [isFaceIdLoading, setIsFaceIdLoading] = useState(false);
    const [livenessDetectorKey, setLivenessDetectorKey] = useState(0);
    const [livenessProgress, setLivenessProgress] = useState({
        command: "",
        message: "",
    });
    const [livenessError, setLivenessError] = useState("");
    const [addressSameAsResidenceAddress, setAddressSameAsResidenceAddress] = useState(true);
    const [addressData, setAddressData] = useState(() => {
        const prefilledZipCode = isDemoReservationLocation ? DEMO_DEFAULT_ADDRESS_ZIP_CODE : "";
        const prefilledHouseNumber = isDemoReservationLocation ? DEMO_DEFAULT_ADDRESS_HOUSE_NUMBER : "";

        return {
            residence: {
                zip_code: prefilledZipCode,
                house_number: prefilledHouseNumber,
                house_number_extension: "",
                street_name: "",
                city: "",
                country: "NL",
            },
            invoice: {
                zip_code: prefilledZipCode,
                house_number: prefilledHouseNumber,
                house_number_extension: "",
                street_name: "",
                city: "",
                country: "NL",
            },
        };
    });
    const [addressLookupLoading, setAddressLookupLoading] = useState({
        residence: false,
        invoice: false,
    });
    const [addressLookupError, setAddressLookupError] = useState("");
    const [addressValidationError, setAddressValidationError] = useState("");
    const [contractFiles, setContractFiles] = useState([]);
    const [activeCustomerId, setActiveCustomerId] = useState(null);
    const [isCustomerRegistered, setIsCustomerRegistered] = useState(false);
    const [isContractFilesLoading, setIsContractFilesLoading] = useState(false);
    const [isContractSigning, setIsContractSigning] = useState(false);
    const [contractError, setContractError] = useState("");
    const [isDemoRegistrationCompleted, setIsDemoRegistrationCompleted] = useState(false);

    const nativeSelfieCaptureInputRef = useRef(null);
    const idsfGroupStartPromiseRef = useRef(null);
    const idsfInitialAppendGroupsRef = useRef(new Set());
    const consentLoggedRef = useRef(false);

    // Once documents/selfie are in progress the browser back button is neutralised:
    // going back re-activates stale capture state (old selfie, expired session).
    const isBackNavigationBlocked = step !== STEP_ACCESS && step !== STEP_ACCOUNT;
    const pushHistoryGuard = useBlockBrowserBack(isBackNavigationBlocked);
    const processedDocumentCaptureResultRef = useRef(null);
    const residenceLookupKeyRef = useRef("");
    const invoiceLookupKeyRef = useRef("");
    const addressLookupInFlightRef = useRef({
        residence: "",
        invoice: "",
    });

    const subsidiaryId = useMemo(() => {
        if (!vehicleInfo?.company) return null;
        const defaultSubsidiaryId = vehicleInfo.company.parent ? vehicleInfo.company.parent.id : vehicleInfo.company.id;
        return resolveWhiteLabelSubsidiaryId(defaultSubsidiaryId);
    }, [vehicleInfo]);

    const hasFrontLicensePhoto = Boolean(frontLicensePhoto);
    const hasSelfiePhoto = Boolean(selfiePhoto);
    const livenessTexts = useMemo(() => ({
        ...DEFAULT_LIVENESS_TEXTS,
        center_face: "Plaats je gezicht in het midden",
        turn_head_left: "Draai je hoofd een beetje naar links",
        turn_head_top: "Kijk een beetje omhoog",
        turn_head_right: "Draai je hoofd een beetje naar rechts",
        turn_head_bottom: "Kijk een beetje omlaag",
        look_to_the_camera: "Kijk recht in de camera",
        keep_looking: "Blijf naar de camera kijken",
        done: "Controle voltooid",
        loading_please_wait: "Laden, even geduld...",
    }), []);
    const allContractsAccepted = useMemo(
        () => contractFiles.every((item) => item.checked),
        [contractFiles]
    );
    const residenceAddressValid = useMemo(() => {
        const data = addressData.residence;
        return Boolean(
            normalizeZipCode(data.zip_code) &&
            String(data.house_number || "").trim() &&
            String(data.street_name || "").trim() &&
            String(data.city || "").trim() &&
            String(data.country || "").trim()
        );
    }, [addressData.residence]);
    const invoiceAddressValid = useMemo(() => {
        if (addressSameAsResidenceAddress) return true;
        const data = addressData.invoice;
        return Boolean(
            normalizeZipCode(data.zip_code) &&
            String(data.house_number || "").trim() &&
            String(data.street_name || "").trim() &&
            String(data.city || "").trim() &&
            String(data.country || "").trim()
        );
    }, [addressData.invoice, addressSameAsResidenceAddress]);
    const addressValid = residenceAddressValid && invoiceAddressValid;

    useEffect(() => {
        forceScrollToTop();
    }, [location.pathname, step]);

    useEffect(() => {
        sendEvent("reserve_register_view");
    }, []);

    useEffect(() => {
        if (isMobileViewport()) return;
        sendEvent("reserve_register_desktop_view");
    }, []);

    useEffect(() => {
        sendEvent(REGISTRATION_STEP_VIEW_EVENTS[step] || "");
    }, [step]);

    useEffect(() => {
        const onResize = () => setIsMobileFlow(isMobileViewport());
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const queryGroupId = getIdsfGroupIdFromQuery(queryParams);
        if (!queryGroupId) return;

        setIdsfGroupId((prev) => prev === queryGroupId ? prev : queryGroupId);
        try {
            window.localStorage.setItem(idsfGroupStorageKey, queryGroupId);
        } catch (_error) {
            // Storage can be restricted in embedded or private browser contexts.
        }
    }, [idsfGroupStorageKey, queryParams]);

    useEffect(() => {
        setAccountData((prev) => ({
            ...prev,
            email: resolvedReservationEmail || prev.email,
        }));
    }, [resolvedReservationEmail]);

    useEffect(() => {
        const normalizedCountryCode = sanitizePhoneDigits(phoneInputData.countryCallingCode) || "31";
        const normalizedNationalNumber = sanitizePhoneDigits(phoneInputData.nationalNumber);
        const combinedNumber = normalizedNationalNumber ? `+${normalizedCountryCode}${normalizedNationalNumber}` : "";

        setAccountData((prev) => (
            prev.phoneNumber === combinedNumber
                ? prev
                : {
                    ...prev,
                    phoneNumber: combinedNumber,
                }
        ));
    }, [phoneInputData.countryCallingCode, phoneInputData.nationalNumber]);

    useEffect(() => {
        if (!addressSameAsResidenceAddress) return;
        setAddressData((prev) => ({
            ...prev,
            invoice: {...prev.residence},
        }));
    }, [addressSameAsResidenceAddress, addressData.residence]);

    useEffect(() => {
        if (step === STEP_SELFIE) return;
        setIsLivenessActive(false);
    }, [step]);

    useEffect(() => {
        if (!isLivenessActive) {
            setCanUseFallbackSelfieCapture(false);
            return undefined;
        }

        setCanUseFallbackSelfieCapture(false);
        const timeoutId = setTimeout(() => {
            setCanUseFallbackSelfieCapture(true);
        }, 10000);

        return () => clearTimeout(timeoutId);
    }, [isLivenessActive, livenessDetectorKey]);

    useEffect(() => {
        if (!vehicleId) return;

        let cancelled = false;

        (async () => {
            setIsVehicleLoading(true);
            setVehicleLoadError("");
            try {
                const response = await detailVehicleAPI({vehicleId});
                if (!cancelled) {
                    setVehicleInfo(response);
                }
            } catch (error) {
                if (!cancelled) {
                    setVehicleLoadError("Voertuiggegevens konden niet worden geladen. Probeer opnieuw.");
                }
            } finally {
                if (!cancelled) {
                    setIsVehicleLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [vehicleId]);

    const validPhone = useMemo(() => {
        const nationalDigits = sanitizePhoneDigits(phoneInputData.nationalNumber);
        return Boolean(accountData.phoneNumber) && (
            isPossiblePhoneNumber(accountData.phoneNumber) ||
            nationalDigits.length >= 7
        );
    }, [accountData.phoneNumber, phoneInputData.nationalNumber]);
    const shouldHighlightPhoneField = useMemo(() => {
        if (!accountData.sendPasswordBySms) return false;
        return requiresManualPhoneInput || !validPhone;
    }, [accountData.sendPasswordBySms, requiresManualPhoneInput, validPhone]);
    const accountValid = useMemo(() => {
        const validEmail = isValidEmail(accountData.email);
        if (accountData.sendPasswordBySms) {
            return validPhone && validEmail && !requiresManualPhoneInput;
        }

        const validPassword = isStrongPassword(accountData.password);
        const samePassword = accountData.password === accountData.repeatPassword && accountData.repeatPassword.length > 0;
        return validPhone && validEmail && validPassword && samePassword;
    }, [accountData, validPhone, requiresManualPhoneInput]);
    const passwordMatchState = useMemo(() => {
        if (accountData.sendPasswordBySms) return "hidden";
        if (!accountData.repeatPassword) return "idle";
        return accountData.password === accountData.repeatPassword ? "match" : "mismatch";
    }, [accountData.sendPasswordBySms, accountData.password, accountData.repeatPassword]);
    const activeCountryFieldMeta = useMemo(() => {
        if (!countrySelectorState.field) return null;
        return DOCUMENT_COUNTRY_FIELD_META[countrySelectorState.field] || null;
    }, [countrySelectorState.field]);
    const filteredCountryOptions = useMemo(() => {
        return filterCountryOptions(countrySelectorState.searchTerm);
    }, [countrySelectorState.searchTerm]);
    const filteredAddressCountryOptions = useMemo(() => {
        return filterCountryOptions(addressCountrySelectorState.searchTerm);
    }, [addressCountrySelectorState.searchTerm]);
    const filteredPhoneCountryOptions = useMemo(() => {
        return filterCountryOptions(phoneCountrySelectorState.searchTerm);
    }, [phoneCountrySelectorState.searchTerm]);
    const openCountrySelector = useCallback((field) => {
        if (!DOCUMENT_COUNTRY_FIELD_META[field]) return;
        setCountrySelectorState({
            isOpen: true,
            field,
            searchTerm: "",
        });
    }, []);
    const closeCountrySelector = useCallback(() => {
        setCountrySelectorState({
            isOpen: false,
            field: "",
            searchTerm: "",
        });
    }, []);
    const onCountrySearchChange = useCallback((event) => {
        const nextValue = event.target?.value || "";
        setCountrySelectorState((prev) => ({
            ...prev,
            searchTerm: nextValue,
        }));
    }, []);
    const selectCountryForActiveField = useCallback((countryCode) => {
        const activeField = countrySelectorState.field;
        if (!activeField || !DOCUMENT_COUNTRY_FIELD_META[activeField]) return;

        setDocumentData((prev) => ({
            ...prev,
            [activeField]: normalizeCountryAlpha2(countryCode),
        }));
        setCountrySelectorState({
            isOpen: false,
            field: "",
            searchTerm: "",
        });
    }, [countrySelectorState.field]);
    const getCountryOptionByAlpha2 = useCallback((value) => {
        const alpha2 = normalizeCountryAlpha2(value);
        if (!alpha2) return null;
        return COUNTRY_OPTIONS_BY_ALPHA2[alpha2] || null;
    }, []);
    const selectedPhoneCountry = useMemo(() => {
        return getCountryOptionByAlpha2(phoneInputData.countryAlpha2) ||
            COUNTRY_OPTIONS_BY_CALLING_CODE[sanitizePhoneDigits(phoneInputData.countryCallingCode)] ||
            COUNTRY_OPTIONS_BY_ALPHA2.NL ||
            COUNTRY_OPTIONS[0];
    }, [getCountryOptionByAlpha2, phoneInputData.countryAlpha2, phoneInputData.countryCallingCode]);
    const openPhoneCountrySelector = useCallback(() => {
        setPhoneCountrySelectorState({
            isOpen: true,
            searchTerm: "",
        });
    }, []);
    const closePhoneCountrySelector = useCallback(() => {
        setPhoneCountrySelectorState({
            isOpen: false,
            searchTerm: "",
        });
    }, []);
    const onPhoneCountrySearchChange = useCallback((event) => {
        const nextValue = event.target?.value || "";
        setPhoneCountrySelectorState((prev) => ({
            ...prev,
            searchTerm: nextValue,
        }));
    }, []);
    const selectPhoneCountry = useCallback((country) => {
        if (!country?.countryCode || !country?.countryCallingCode) return;
        setPhoneInputData((prev) => ({
            ...prev,
            countryAlpha2: country.countryCode,
            countryCallingCode: country.countryCallingCode,
        }));
        setIsPhoneAutoGenerated(false);
        setPhoneCountrySelectorState({
            isOpen: false,
            searchTerm: "",
        });
    }, []);
    const ensureIdsfGroupId = useCallback(async () => {
        const queryGroupId = getIdsfGroupIdFromQuery(queryParams);
        const normalizedCurrentGroupId = queryGroupId || normalizeIdsfGroupId(idsfGroupId);
        if (normalizedCurrentGroupId) {
            setIdsfGroupId((prev) => prev === normalizedCurrentGroupId ? prev : normalizedCurrentGroupId);
            try {
                window.localStorage.setItem(idsfGroupStorageKey, normalizedCurrentGroupId);
            } catch (_error) {
                // Storage can be restricted in embedded or private browser contexts.
            }
            return normalizedCurrentGroupId;
        }

        try {
            const storedGroupId = normalizeIdsfGroupId(window.localStorage.getItem(idsfGroupStorageKey));
            if (storedGroupId) {
                setIdsfGroupId(storedGroupId);
                return storedGroupId;
            }
        } catch (_error) {
            // Storage can be restricted in embedded or private browser contexts.
        }

        if (!idsfGroupStartPromiseRef.current) {
            idsfGroupStartPromiseRef.current = idsfTransactionGroupStartAPI({})
                .then((response) => {
                    const nextGroupId = getIdsfGroupIdFromStartResponse(response);
                    if (!nextGroupId) {
                        throw new Error("idsf_group_id ontbreekt in de response");
                    }

                    setIdsfGroupId(nextGroupId);
                    try {
                        window.localStorage.setItem(idsfGroupStorageKey, nextGroupId);
                    } catch (_error) {
                        // Storage can be restricted in embedded or private browser contexts.
                    }
                    return nextGroupId;
                })
                .finally(() => {
                    idsfGroupStartPromiseRef.current = null;
                });
        }

        return await idsfGroupStartPromiseRef.current;
    }, [idsfGroupId, idsfGroupStorageKey, queryParams]);

    const appendIdsfTransactionGroup = useCallback(async (payload = {}) => {
        try {
            const currentGroupId = await ensureIdsfGroupId();
            await idsfTransactionGroupAppendAPI({
                groupId: currentGroupId,
                payload,
            });
            return currentGroupId;
        } catch (error) {
            console.warn("IDSF transaction group append failed", error);
            return "";
        }
    }, [ensureIdsfGroupId]);

    useEffect(() => {
        if (!idsfGroupId) {
            ensureIdsfGroupId().catch((error) => {
                console.warn("IDSF transaction group start failed", error);
            });
        }
    }, [ensureIdsfGroupId, idsfGroupId]);

    useEffect(() => {
        if (!consentConfirmedUpfront || consentLoggedRef.current) return;
        consentLoggedRef.current = true;
        appendIdsfTransactionGroup({
            "logs.registration_step": "consent_confirmed",
        });
    }, [appendIdsfTransactionGroup, consentConfirmedUpfront]);

    useEffect(() => {
        const normalizedGroupId = normalizeIdsfGroupId(idsfGroupId);
        if (!normalizedGroupId || idsfInitialAppendGroupsRef.current.has(normalizedGroupId)) return;

        idsfInitialAppendGroupsRef.current.add(normalizedGroupId);
        appendIdsfTransactionGroup({
            "logs.registration_source": "website",
            "logs.registration_context": "reservation_register",
            "website.vehicle_id": String(vehicleId || ""),
            "website.location_id": String(locationId || ""),
            "website.flow_device": isMobileFlow ? "mobile" : "desktop",
            "website.public_base": publicWrapperBase,
            "reservation.from": reservationContext.from || "",
            "reservation.to": reservationContext.to || "",
        });
    }, [
        appendIdsfTransactionGroup,
        idsfGroupId,
        isMobileFlow,
        locationId,
        publicWrapperBase,
        reservationContext.from,
        reservationContext.to,
        vehicleId,
    ]);

    const resolveDocumentCaptureReference = useCallback(async () => {
        try {
            const currentGroupId = await ensureIdsfGroupId();
            if (currentGroupId) return `idsf:${currentGroupId}`;
        } catch (_error) {
            // The capture session does not depend on the IDSF group; fall back to a local reference.
        }
        return `newway:${vehicleId || "vehicle"}:${locationId || "location"}:${Date.now()}`;
    }, [ensureIdsfGroupId, locationId, vehicleId]);

    const documentCapture = useDocumentCaptureSession({
        subsidiaryId,
        active: step === STEP_DOCUMENT_CAPTURE,
        resolveReference: resolveDocumentCaptureReference,
    });
    const {
        start: startDocumentCapture,
        reset: resetDocumentCapture,
        fail: failDocumentCapture,
        pollNow: pollDocumentCaptureNow,
    } = documentCapture;

    useEffect(() => {
        if (step !== STEP_DOCUMENT_CAPTURE) return;
        if (!subsidiaryId) return;
        if (documentCapture.phase !== DOCUMENT_CAPTURE_PHASE_IDLE) return;
        startDocumentCapture();
    }, [documentCapture.phase, startDocumentCapture, step, subsidiaryId]);

    const goToReservationConfirm = (extraParams = {}) => {
        const search = new URLSearchParams({
            from: reservationContext.from || "",
            to: reservationContext.to || "",
            expectedKm: reservationContext.expectedKm || "",
            options: reservationContext.options || "",
            products: reservationContext.products || "",
            email: accountData.email || resolvedReservationEmail || "",
            ...extraParams,
        });

        navigate({
            pathname: `/reservation-confirm/${vehicleId}/${locationId}`,
            search: appendPublicWrapperBaseToSearch(search, location.search).toString(),
        });
    };
    const reservationConfirmLink = useMemo(() => ({
        pathname: `/reservation-confirm/${vehicleId}/${locationId}`,
        search: appendPublicWrapperBaseToSearch(new URLSearchParams({
            from: reservationContext.from || "",
            to: reservationContext.to || "",
            expectedKm: reservationContext.expectedKm || "",
            options: reservationContext.options || "",
            products: reservationContext.products || "",
            email: accountData.email || resolvedReservationEmail || "",
        }), location.search).toString(),
    }), [
        vehicleId,
        locationId,
        location.search,
        reservationContext.from,
        reservationContext.to,
        reservationContext.expectedKm,
        reservationContext.options,
        reservationContext.products,
        resolvedReservationEmail,
        accountData.email,
    ]);

    const onLoginInstead = () => {
        goToReservationConfirm({login: "1"});
    };

    const onOpenConsentModal = () => {
        setConsentIdChecked(true);
        setConsentPrivacyChecked(true);
        setShowConsentModal(true);
    };

    const onRegisterConsentConfirm = () => {
        if (!consentIdChecked || !consentPrivacyChecked) return;
        setShowConsentModal(false);
        appendIdsfTransactionGroup({
            "logs.registration_step": "consent_confirmed",
        });
        setStep(STEP_ACCOUNT);
    };

    const logDocumentDataToIdsf = useCallback((data) => {
        appendIdsfTransactionGroup({
            "personal_data.lastName": data.surname,
            "personal_data.firstName": data.firstName,
            "ocr.drivers_license.number": data.licenseNumber,
            "ocr.drivers_license.expirationDate": normalizeDocumentDate(data.expirationDate),
            "ocr.drivers_license.birthDate": normalizeDocumentDate(data.birthDate),
            "ocr.drivers_license.categories": data.categoryB ? "B" : "",
            "ocr.drivers_license.country": normalizeDocumentCountry(data.issueCountry),
        });
    }, [appendIdsfTransactionGroup]);

    const applyDocumentCaptureResult = useCallback(async (captureResult) => {
        const images = Array.isArray(captureResult?.images) ? captureResult.images : [];
        const licenseImage = findDocumentCaptureImage(images, {documentType: "drivers_license", side: "front"});
        const selfieImage = findDocumentCaptureImage(images, {documentType: "selfie"});

        const rawLicenseDataUrl = toDocumentCaptureImageDataUrl(licenseImage);
        if (!rawLicenseDataUrl) {
            throw new Error("De rijbewijsfoto ontbreekt in het resultaat van de documentcontrole.");
        }

        // Capture images are full resolution; bring them down to the same limits as
        // the native camera flow before they are sent to registration/document upload.
        const compressCaptureImage = async (dataUrl) => {
            if (!dataUrl) return "";
            try {
                return await compressDocumentPhotoToDataUrl(await dataUrlToBlob(dataUrl));
            } catch (_error) {
                return dataUrl;
            }
        };
        const [licenseDataUrl, selfieDataUrl] = await Promise.all([
            compressCaptureImage(rawLicenseDataUrl),
            compressCaptureImage(toDocumentCaptureImageDataUrl(selfieImage)),
        ]);

        const fields = extractDocumentCaptureRecognitionFields(captureResult?.recognition);
        const hasRecognizedFields = Object.keys(fields).length > 0;
        const recognizedDocumentData = hasRecognizedFields
            ? buildDocumentDataFromOcrFields(fields)
            : EMPTY_DOCUMENT_DATA;
        const isRecognitionComplete = hasRecognizedFields && isDocumentDataComplete(recognizedDocumentData);

        setFrontLicensePhoto(licenseDataUrl);
        setSelfiePhoto(selfieDataUrl);
        setLivenessError("");
        setLivenessProgress({
            command: selfieDataUrl ? "document_capture" : "",
            message: selfieDataUrl ? "Selfie is ontvangen via de documentcontrole." : "",
        });
        setDocumentData(recognizedDocumentData);
        setDocumentOcrError(
            hasRecognizedFields
                ? ""
                : "Gegevens konden niet automatisch worden uitgelezen. Vul de velden handmatig in."
        );
        appendIdsfTransactionGroup({
            "logs.registration_step": selfieDataUrl
                ? "document_capture_completed"
                : "document_capture_completed_without_selfie",
        });

        if (isRecognitionComplete) {
            logDocumentDataToIdsf(recognizedDocumentData);
            appendIdsfTransactionGroup({
                "logs.registration_step": "document_data_auto_confirmed",
            });
            setStep(selfieDataUrl ? STEP_ADDRESS : STEP_SELFIE);
            return;
        }

        setStep(STEP_DOCUMENT_DATA);
    }, [appendIdsfTransactionGroup, logDocumentDataToIdsf]);

    useEffect(() => {
        if (documentCapture.phase !== DOCUMENT_CAPTURE_PHASE_DELIVERED || !documentCapture.result) return undefined;
        if (processedDocumentCaptureResultRef.current === documentCapture.result) return undefined;
        processedDocumentCaptureResultRef.current = documentCapture.result;

        setIsDocumentCaptureResultProcessing(true);

        // Runs to completion even if the effect re-subscribes: the processed
        // result is tracked in a ref so the same payload is never applied twice.
        (async () => {
            try {
                await applyDocumentCaptureResult(documentCapture.result);
                resetDocumentCapture();
            } catch (error) {
                failDocumentCapture(error?.message || "Documentcontrole kon niet worden verwerkt. Start een nieuwe controle.");
                appendIdsfTransactionGroup({
                    "logs.registration_step": `document_capture_error: ${error?.message || "unknown"}`,
                });
            } finally {
                setIsDocumentCaptureResultProcessing(false);
            }
        })();

        return undefined;
    }, [
        appendIdsfTransactionGroup,
        applyDocumentCaptureResult,
        documentCapture.phase,
        documentCapture.result,
        failDocumentCapture,
        resetDocumentCapture,
    ]);

    const onDocumentCaptureMessage = useCallback((eventName) => {
        if (eventName === "completed") {
            appendIdsfTransactionGroup({
                "logs.registration_step": "document_capture_iframe_completed",
            });
            pollDocumentCaptureNow();
        }
    }, [appendIdsfTransactionGroup, pollDocumentCaptureNow]);

    const onDocumentCaptureRetry = useCallback(() => {
        setDocumentOcrError("");
        startDocumentCapture();
    }, [startDocumentCapture]);

    const onDocumentCaptureBack = useCallback(() => {
        resetDocumentCapture();
        setStep(STEP_DOCUMENT_INTRO);
    }, [resetDocumentCapture]);

    const openFallbackSelfieCapture = useCallback(() => {
        if (!nativeSelfieCaptureInputRef.current) return;

        setLivenessError("");
        setIsLivenessActive(false);
        setCanUseFallbackSelfieCapture(false);
        nativeSelfieCaptureInputRef.current.value = "";
        nativeSelfieCaptureInputRef.current.click();
    }, []);

    const onNativeSelfieCaptureFileChange = useCallback(async (event) => {
        const file = event.target?.files?.[0];
        if (!file) return;

        setIsSelfieCaptureProcessing(true);
        setLivenessError("");
        try {
            const dataUrl = await compressDocumentPhotoToDataUrl(file);
            if (!dataUrl.startsWith("data:")) {
                throw new Error("Ongeldig afbeeldingsbestand.");
            }

            setSelfiePhoto(dataUrl);
            setLivenessProgress({
                command: "selfie_manual_capture",
                message: "Selfie is vastgelegd.",
            });
            appendIdsfTransactionGroup({
                "logs.registration_step": "selfie_manual_capture",
            });
            setStep(STEP_ADDRESS);
        } catch (error) {
            setLivenessError(error?.message || "Selfie kon niet worden vastgelegd. Probeer opnieuw.");
        } finally {
            setIsSelfieCaptureProcessing(false);
        }
    }, [appendIdsfTransactionGroup]);

    const setAddressField = (type, field, value) => {
        if (field === "zip_code" || field === "house_number" || field === "country") {
            if (type === "residence") {
                residenceLookupKeyRef.current = "";
                addressLookupInFlightRef.current.residence = "";
                if (addressSameAsResidenceAddress) {
                    invoiceLookupKeyRef.current = "";
                    addressLookupInFlightRef.current.invoice = "";
                }
            } else {
                invoiceLookupKeyRef.current = "";
                addressLookupInFlightRef.current.invoice = "";
            }
        }

        setAddressData((prev) => {
            const updatedBlock = {
                ...prev[type],
                [field]: value,
            };
            const next = {
                ...prev,
                [type]: updatedBlock,
            };
            if (type === "residence" && addressSameAsResidenceAddress) {
                next.invoice = {...updatedBlock};
            }
            return next;
        });
    };
    const openAddressCountrySelector = (addressType) => {
        if (addressType !== "residence" && addressType !== "invoice") return;
        setAddressCountrySelectorState({
            isOpen: true,
            addressType,
            searchTerm: "",
        });
    };
    const closeAddressCountrySelector = () => {
        setAddressCountrySelectorState({
            isOpen: false,
            addressType: "",
            searchTerm: "",
        });
    };
    const onAddressCountrySearchChange = (event) => {
        const nextValue = event.target?.value || "";
        setAddressCountrySelectorState((prev) => ({
            ...prev,
            searchTerm: nextValue,
        }));
    };
    const selectAddressCountry = (countryCode) => {
        const addressType = addressCountrySelectorState.addressType;
        if (addressType !== "residence" && addressType !== "invoice") return;
        setAddressField(addressType, "country", normalizeCountryAlpha2(countryCode));
        setAddressCountrySelectorState({
            isOpen: false,
            addressType: "",
            searchTerm: "",
        });
    };

    const lookupAddressByPostalCode = useCallback(async ({type, zipCode, houseNumber, country}) => {
        const normalizedZip = normalizeZipCode(zipCode);
        const normalizedHouseNumber = String(houseNumber || "").trim();
        const normalizedCountry = String(country || "").toUpperCase();
        if (normalizedCountry !== "NL") return;
        if (!normalizedZip || !normalizedHouseNumber) return;

        const currentKey = `${normalizedZip}::${normalizedHouseNumber}`;
        const keyRef = type === "residence" ? residenceLookupKeyRef : invoiceLookupKeyRef;
        const inFlightKey = addressLookupInFlightRef.current[type];

        if (inFlightKey === currentKey) return;
        if (keyRef.current === currentKey) return;

        addressLookupInFlightRef.current[type] = currentKey;

        setAddressLookupError("");
        setAddressLookupLoading((prev) => ({...prev, [type]: true}));

        try {
            const response = await postalCodeLookupAPI({
                zipCode: normalizedZip,
                houseNumber: normalizedHouseNumber,
            });
            setAddressData((prev) => {
                const updatedBlock = {
                    ...prev[type],
                    zip_code: normalizedZip,
                    house_number: normalizedHouseNumber,
                    street_name: response?.street || prev[type].street_name,
                    city: response?.city || prev[type].city,
                    country: "NL",
                };
                const next = {
                    ...prev,
                    [type]: updatedBlock,
                };
                if (type === "residence" && addressSameAsResidenceAddress) {
                    next.invoice = {...updatedBlock};
                }
                return next;
            });
            keyRef.current = currentKey;
        } catch (error) {
            setAddressLookupError("Adres kon niet automatisch worden opgehaald. Vul straat en plaats handmatig in.");
        } finally {
            if (addressLookupInFlightRef.current[type] === currentKey) {
                addressLookupInFlightRef.current[type] = "";
            }
            setAddressLookupLoading((prev) => ({...prev, [type]: false}));
        }
    }, [addressSameAsResidenceAddress]);

    useEffect(() => {
        if (step !== STEP_ADDRESS) return;

        const zipCode = normalizeZipCode(addressData.residence.zip_code);
        const houseNumber = String(addressData.residence.house_number || "").trim();
        const country = String(addressData.residence.country || "").toUpperCase();

        if (country !== "NL") return;
        if (zipCode.length < 6 || !houseNumber) return;

        lookupAddressByPostalCode({
            type: "residence",
            zipCode,
            houseNumber,
            country,
        });
    }, [step, addressData.residence.zip_code, addressData.residence.house_number, addressData.residence.country, lookupAddressByPostalCode]);

    useEffect(() => {
        if (step !== STEP_ADDRESS) return;
        if (addressSameAsResidenceAddress) return;

        const zipCode = normalizeZipCode(addressData.invoice.zip_code);
        const houseNumber = String(addressData.invoice.house_number || "").trim();
        const country = String(addressData.invoice.country || "").toUpperCase();

        if (country !== "NL") return;
        if (zipCode.length < 6 || !houseNumber) return;

        lookupAddressByPostalCode({
            type: "invoice",
            zipCode,
            houseNumber,
            country,
        });
    }, [step, addressSameAsResidenceAddress, addressData.invoice.zip_code, addressData.invoice.house_number, addressData.invoice.country, lookupAddressByPostalCode]);

    const onAccountContinue = () => {
        if (!accountValid || !subsidiaryId) return;
        setShowAccountConfirmModal(true);
    };

    const onAccountConfirmYes = async () => {
        if (!subsidiaryId) return;

        setIsAccountCheckLoading(true);
        setAccountCheckError("");
        setIsEmailValidationUnavailable(false);

        const personalContactPayload = {
            "personal_data.phone": accountData.phoneNumber || "",
            "personal_data.email": accountData.email || "",
        };

        try {
            let emailValidation;
            try {
                emailValidation = await validateEmailAPI({email: accountData.email});
            } catch (error) {
                setIsEmailValidationUnavailable(true);
                setShowEmailNotValidModal(true);
                setShowAccountConfirmModal(false);
                return;
            }

            if (emailValidation?.is_valid === "invalid") {
                setIsEmailValidationUnavailable(false);
                appendIdsfTransactionGroup({
                    ...personalContactPayload,
                    email_valid: "false",
                });
                setShowEmailNotValidModal(true);
                setShowAccountConfirmModal(false);
                return;
            }
            appendIdsfTransactionGroup({
                ...personalContactPayload,
                email_valid: "true",
            });

            const customerLookup = await searchUserAPI({
                subsidiaryId,
                email: accountData.email,
                mobilePhoneNumber: accountData.phoneNumber,
            });

            if (customerLookup?.customer_already_exists === true) {
                appendIdsfTransactionGroup({
                    "mobielrijden.double_registration": "yes",
                    "mobielrijden.double_registration_type": customerLookup?.customer_has_another_subsidiary === true ? "another_subsidiary" : "email_or_phone",
                });
                setShowAccountConfirmModal(false);

                if (customerLookup?.customer_has_another_subsidiary === true) {
                    setShowAnotherSubsidiaryModal(true);
                    return;
                }

                setShowCustomerExistsModal(true);
                return;
            }

            setShowAccountConfirmModal(false);
            appendIdsfTransactionGroup({
                "logs.registration_step": "account_validated",
            });
            setStep(STEP_DOCUMENT_INTRO);
        } catch (error) {
            setAccountCheckError("Het controleren van je gegevens is mislukt. Probeer opnieuw.");
            setShowAccountConfirmModal(false);
        } finally {
            setIsAccountCheckLoading(false);
        }
    };

    const canContinueDocumentData = hasFrontLicensePhoto && !!documentData.categoryB;

    const onDocumentDataContinue = () => {
        if (!canContinueDocumentData) return;
        logDocumentDataToIdsf(documentData);
        setShowDocumentConfirmModal(true);
    };

    const startLivenessDetection = useCallback(() => {
        setSelfiePhoto("");
        setCanUseFallbackSelfieCapture(false);
        setLivenessError("");
        setLivenessProgress({
            command: "",
            message: "",
        });
        setLivenessDetectorKey((prev) => prev + 1);
        setIsLivenessActive(true);
    }, []);

    const onLivenessComplete = useCallback(async ({base64, dataUrl}) => {
        const normalizedSelfie = dataUrl || (base64 ? `data:image/jpeg;base64,${base64}` : "");
        if (!normalizedSelfie) {
            setLivenessError("Selfie kon niet worden vastgelegd. Probeer opnieuw.");
            setIsLivenessActive(false);
            return;
        }

        const documentImage = extractBase64ImageData(frontLicensePhoto);
        const selfieImage = extractBase64ImageData(normalizedSelfie);
        if (!documentImage?.data || !selfieImage?.data) {
            setLivenessError("Face ID kon niet worden gestart omdat document- of selfiefoto ontbreekt.");
            setIsLivenessActive(false);
            return;
        }

        setIsLivenessActive(false);
        setIsFaceIdLoading(true);
        setLivenessError("");
        setLivenessProgress({
            command: "face_id",
            message: "Face ID controle wordt uitgevoerd...",
        });

        try {
            const currentGroupId = await ensureIdsfGroupId();
            const faceIdResponse = await faceIdAPI({
                image1: {
                    mime_type: documentImage.mimeType,
                    data: documentImage.data,
                },
                image2: {
                    mime_type: selfieImage.mimeType,
                    data: selfieImage.data,
                },
                idsfGroupId: currentGroupId,
            });

            if (faceIdResponse?.error) {
                throw new Error(faceIdResponse.error?.message || "face_id_error");
            }
            if (faceIdResponse?.is_identical === false) {
                throw new Error("Face ID kon je selfie niet matchen met je document. Probeer opnieuw.");
            }

            setSelfiePhoto(normalizedSelfie);
            setLivenessError("");
            setIsLivenessActive(false);
            setCanUseFallbackSelfieCapture(false);
            setLivenessProgress({
                command: "liveness_finish",
                message: "Selfie is succesvol vastgelegd.",
            });
            appendIdsfTransactionGroup({
                "logs.registration_step": "face_id_success",
            });
            setStep(STEP_ADDRESS);
        } catch (error) {
            setLivenessError(error?.message || "Face ID controle is mislukt. Probeer opnieuw.");
            setIsLivenessActive(false);
            appendIdsfTransactionGroup({
                "logs.registration_step": `face_id_error: ${error?.message || "unknown"}`,
            });
        } finally {
            setIsFaceIdLoading(false);
        }
    }, [appendIdsfTransactionGroup, ensureIdsfGroupId, frontLicensePhoto]);

    const onLivenessProgress = useCallback((response) => {
        setLivenessProgress({
            command: response?.command || "",
            message: response?.message || "",
        });
    }, []);

    const onLivenessError = useCallback(() => {
        setLivenessError("Liveness-check kon niet worden uitgevoerd. Controleer cameratoegang en probeer opnieuw.");
        setIsLivenessActive(false);
        setCanUseFallbackSelfieCapture(false);
    }, []);

    const buildRegistrationPayload = (groupIdOverride = idsfGroupId) => {
        const selfieImage = extractBase64ImageData(selfiePhoto);
        const normalizedGroupId = normalizeIdsfGroupId(groupIdOverride);
        const residenceAddress = {
            zip_code: normalizeZipCode(addressData.residence.zip_code),
            house_number: String(addressData.residence.house_number || "").trim(),
            house_number_extension: String(addressData.residence.house_number_extension || "").trim(),
            street_name: String(addressData.residence.street_name || "").trim(),
            city: String(addressData.residence.city || "").trim(),
            country: String(addressData.residence.country || "NL").trim().toUpperCase(),
        };
        const invoiceSource = addressSameAsResidenceAddress ? residenceAddress : addressData.invoice;
        const invoiceAddress = {
            zip_code: normalizeZipCode(invoiceSource.zip_code),
            house_number: String(invoiceSource.house_number || "").trim(),
            house_number_extension: String(invoiceSource.house_number_extension || "").trim(),
            street_name: String(invoiceSource.street_name || "").trim(),
            city: String(invoiceSource.city || "").trim(),
            country: String(invoiceSource.country || "NL").trim().toUpperCase(),
        };

        const basePayload = {
            registration_source: "website",
            idsf_group_id: normalizedGroupId || undefined,
            customer_type: "private",
            email_address: accountData.email,
            mobile_phone_number: accountData.phoneNumber,
            can_store_selfie: !!agreedSelfieStorage,
            first_name: documentData.firstName,
            last_name: documentData.surname,
            date_of_birth: normalizeDocumentDate(documentData.birthDate),
            nationality: normalizeDocumentCountry(documentData.nationality),
            country_of_birth: normalizeDocumentCountry(documentData.birthCountry),
            drivers_license_number: documentData.licenseNumber,
            drivers_license_expiration_date: normalizeDocumentDate(documentData.expirationDate),
            drivers_license_category_b: !!documentData.categoryB,
            photo: selfieImage
                ? {
                    data: selfieImage.data,
                    mime_type: selfieImage.mimeType,
                }
                : undefined,
            address: invoiceAddress,
            residence_address: residenceAddress,
        };

        if (!accountData.sendPasswordBySms) {
            return {
                ...basePayload,
                generate_ephemeral_password: false,
                password: accountData.password,
            };
        }

        return {
            ...basePayload,
            generate_ephemeral_password: true,
        };
    };

    const buildDriversLicenseDocumentPayload = () => {
        const frontImage = extractBase64ImageData(frontLicensePhoto);
        if (!frontImage) {
            return null;
        }

        const images = [
            {
                side: "front",
                data: frontImage.data,
                mime_type: frontImage.mimeType,
                light_type: "white",
            },
        ];

        return {
            type: "drivers_license",
            number: String(documentData.licenseNumber || "").trim(),
            expiration_date: normalizeDocumentDate(documentData.expirationDate),
            country: normalizeDocumentCountry(documentData.issueCountry),
            drivers_license_categories: documentData.categoryB ? ["B"] : [],
            images,
        };
    };

    const uploadRegistrationDocuments = async ({authToken}) => {
        const driversLicensePayload = buildDriversLicenseDocumentPayload();
        if (!driversLicensePayload) return;

        const response = await uploadUserDocumentAPI({
            authToken,
            subsidiaryId,
            payload: driversLicensePayload,
        });

        if (response?.error) {
            throw new Error(response.error?.message || "upload_document_error");
        }
    };

    const loadContractFiles = async () => {
        setIsContractFilesLoading(true);
        setContractError("");
        try {
            const whiteLabelContractFiles = WHITE_LABEL_CONTRACT_FILES[resolveWhiteLabelThemeId()];
            if (whiteLabelContractFiles) {
                setContractFiles(whiteLabelContractFiles.map((item) => ({...item})));
                setStep(STEP_CONTRACT);
                return;
            }

            const contractResponse = await getContractAPI({subsidiaryId});
            const contractList = Object.keys(contractResponse || {})
                .filter((fileType) => Boolean(contractResponse[fileType]))
                .map((fileType) => ({
                    type: fileType,
                    url: contractResponse[fileType],
                    checked: true,
                }));
            setContractFiles(contractList);
            setStep(STEP_CONTRACT);
        } catch (error) {
            setContractError("Contractbestanden konden niet worden geladen. Probeer opnieuw.");
        } finally {
            setIsContractFilesLoading(false);
        }
    };

    const onAddressContinue = async () => {
        if (!subsidiaryId || !hasSelfiePhoto || isRegistrationLoading) return;

        if (!addressValid) {
            setAddressValidationError("Vul alle verplichte adresvelden in om verder te gaan.");
            return;
        }

        if (isCustomerRegistered) {
            await loadContractFiles();
            return;
        }

        setAddressValidationError("");
        setAddressLookupError("");
        setRegistrationError("");
        setContractError("");
        setIsRegistrationLoading(true);

        try {
            const currentIdsfGroupId = await appendIdsfTransactionGroup({
                "personal_data.phone": accountData.phoneNumber || "",
                "personal_data.email": accountData.email || "",
                "personal_data.address.zip": addressData.residence?.zip_code,
                "personal_data.address.houseNumber": addressData.residence?.house_number,
                "personal_data.address.street": addressData.residence?.street_name,
                "personal_data.address.city": addressData.residence?.city,
                "personal_data.address.country": addressData.residence?.country,
            });

            const registerResponse = await registerClientAPI({
                subsidiaryId,
                payload: buildRegistrationPayload(currentIdsfGroupId),
            });

            if (registerResponse?.error) {
                throw new Error(registerResponse.error?.message || "registration_error");
            }

            appendIdsfTransactionGroup({
                "logs.registration_step": "registration_success",
            });

            const authToken = registerResponse?.token;
            if (!authToken) {
                throw new Error("missing_registration_token");
            }

            if (typeof window !== "undefined" && window.localStorage) {
                window.localStorage.setItem("token", authToken);
            }

            let documentUploadWarning = "";
            try {
                await uploadRegistrationDocuments({authToken});
            } catch (error) {
                documentUploadWarning = "Je account is aangemaakt, maar het uploaden van je rijbewijsfoto's is mislukt.";
            }

            const customers = await getCustomersAPI({authToken, subsidiaryId});
            const activeCustomer = Array.isArray(customers) && customers.length ? customers[0] : null;
            if (!activeCustomer?.id) {
                throw new Error("missing_customer");
            }
            setActiveCustomerId(activeCustomer.id);
            // Bind the freshly registered customer id to analytics, so the
            // rest of the flow and the parent wrapper attribute to this user.
            setUserId(activeCustomer.id);
            setIsCustomerRegistered(true);

            await loadContractFiles();
            if (documentUploadWarning) {
                appendIdsfTransactionGroup({
                    "logs.registration_step": "upload_documents_warning",
                });
                setContractError(documentUploadWarning);
            }
        } catch (error) {
            appendIdsfTransactionGroup({
                "logs.registration_step": `registration_error: ${error?.message || "unknown"}`,
            });
            setRegistrationError("Registratie is mislukt. Controleer je gegevens en probeer opnieuw.");
        } finally {
            setIsRegistrationLoading(false);
        }
    };

    const onSignContract = async () => {
        if (!subsidiaryId || !activeCustomerId || isContractSigning || isContractFilesLoading) return;
        if (!allContractsAccepted) return;

        const authToken = typeof window !== "undefined" && window.localStorage
            ? window.localStorage.getItem("token")
            : "";
        if (!authToken) {
            setContractError("Sessie is verlopen. Registreer opnieuw om verder te gaan.");
            return;
        }

        setContractError("");
        setIsContractSigning(true);

        try {
            const response = await signContractAPI({
                authToken,
                subsidiaryId,
                customerId: activeCustomerId,
            });

            if (response?.error) {
                throw new Error(response.error?.message || "contract_sign_error");
            }

            appendIdsfTransactionGroup({
                "logs.registration_step": "contract_signed",
            });

            if (isDemoReservationLocation) {
                setIsDemoRegistrationCompleted(true);
                return;
            }
            goToReservationConfirm({registered: "1"});
        } catch (error) {
            appendIdsfTransactionGroup({
                "logs.registration_step": `contract_sign_error: ${error?.message || "unknown"}`,
            });
            setContractError("Je account is aangemaakt, maar het contract kon niet worden ondertekend. Probeer opnieuw.");
        } finally {
            setIsContractSigning(false);
        }
    };

    const renderDemoRegistrationSuccess = () => (
        <div className="kav-panel kav-register-mobile kav-register-mobile--demoSuccess">
            <div className="kav-demo-register-success">
                <div className="kav-demo-register-success__icon" aria-hidden="true">
                    <i className="bi bi-person-vcard"></i>
                    <span className="kav-demo-register-success__check">
                        <i className="bi bi-check-lg"></i>
                    </span>
                </div>
                <div className="kav-demo-register-success__content">
                    <h2>Uw registratie is goedgekeurd.</h2>
                    <p className="kav-demo-register-success__lead">
                        U kunt inloggen om de reservering definitief te maken door op onderstaande knop te klikken.
                    </p>
                    <p>
                        Bij een volgende reservering hoeft u dus niet meer uw rijbewijs te laten checken. Gebruik uw emailadres en het wachtwoord dat u heeft aangemaakt of per sms heeft ontvangen om in te loggen.
                    </p>
                    <div className="kav-demo-register-success__actions">
                        <button
                            type="button"
                            className="kav-step-actions__btn kav-step-actions__btn--primary"
                            onClick={() => goToReservationConfirm({registered: "1"})}
                        >
                            {isMobileFlow ? "Verder op mobiel" : "Verder op de computer"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAccessStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Toegang tot account</h2>
            <p className="kav-register-mobile__text">
                Maak een nieuw account aan om je reservering af te ronden.
            </p>

            <div className="kav-auth-actions kav-register-mobile__actions kav-auth-actions--mobile-reverse">
                <button
                    type="button"
                    className="kav-step-actions__btn kav-step-actions__btn--primary"
                    onClick={onOpenConsentModal}
                >
                    Registreren
                </button>
                <button
                    type="button"
                    className="kav-step-actions__btn kav-step-actions__btn--ghost"
                    onClick={onLoginInstead}
                >
                    Terug
                </button>
            </div>

            {showConsentModal && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setShowConsentModal(false)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <label className="kav-register-mobile__check">
                            <input
                                type="checkbox"
                                checked={consentIdChecked}
                                onChange={(event) => setConsentIdChecked(event.target.checked)}
                            />
                            <span>Ik verklaar dat mijn geuploade ID echt is.</span>
                        </label>
                        <label className="kav-register-mobile__check">
                            <input
                                type="checkbox"
                                checked={consentPrivacyChecked}
                                onChange={(event) => setConsentPrivacyChecked(event.target.checked)}
                            />
                            <span>Ik ga akkoord met de verwerking van mijn persoonsgegevens.</span>
                        </label>
                        <div className="kav-auth-actions kav-auth-actions--mobile-reverse">

                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                disabled={!consentIdChecked || !consentPrivacyChecked}
                                onClick={onRegisterConsentConfirm}
                            >
                                Bevestigen
                            </button>

                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => setShowConsentModal(false)}
                            >
                                Annuleren
                            </button>
                        </div>
                        <p className="kav-register-mobile__help">
                            Vragen? Bel ons op <a href="tel:+31880883434">+31 88 088 3434</a>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );

    const renderAccountStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Accountinformatie</h2>

                        {isAccountCheckLoading && (
                            <div className="kav-callout kav-auth-status">
                                <span className="kav-spinner" aria-hidden="true"></span>
                                <span>Gegevens worden gecontroleerd...</span>
                            </div>
                        )}

                        {accountCheckError && (
                            <div className="kav-callout kav-auth-error">
                                {accountCheckError}
                            </div>
                        )}

                        <div className="kav-register-mobile__fieldRow">
                            <div className="kav-input-field">
                                <label className="kav-input-field__label" htmlFor="register-phone-national">
                                    Telefoon
                                </label>
                                <div className={`kav-register-mobile__phoneRow ${shouldHighlightPhoneField ? "is-invalid" : ""}`}>
                                    <button
                                        type="button"
                                        className="kav-phone-composite__country"
                                        onClick={openPhoneCountrySelector}
                                        aria-haspopup="dialog"
                                        aria-expanded={phoneCountrySelectorState.isOpen}
                                    >
                                        <span className="kav-phone-composite__flag" aria-hidden="true">{selectedPhoneCountry?.flag || "🏳️"}</span>
                                        <span className="kav-phone-composite__code">+{sanitizePhoneDigits(phoneInputData.countryCallingCode) || "31"}</span>
                                        <i className="bi bi-chevron-down" aria-hidden="true"></i>
                                    </button>
                                    <input
                                        id="register-phone-national"
                                        className="kav-phone-composite__input"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel-national"
                                        placeholder="612345678"
                                        value={phoneInputData.nationalNumber}
                                        onChange={(event) => {
                                            const nextDigits = sanitizePhoneDigits(event.target.value);
                                            setPhoneInputData((prev) => ({
                                                ...prev,
                                                nationalNumber: nextDigits,
                                            }));
                                            setIsPhoneAutoGenerated(false);
                                            if (nextDigits.length > 0) {
                                                setRequiresManualPhoneInput(false);
                                            }
                                        }}
                                    />
                                </div>
                                {shouldHighlightPhoneField && (
                                    <div className="kav-register-mobile__phoneError">
                                        Vul je echte telefoonnummer in om een tijdelijk wachtwoord per sms te ontvangen.
                                    </div>
                                )}
                            </div>
                        </div>

                        {phoneCountrySelectorState.isOpen && (
                            <div className="kav-simple-modal" role="dialog" aria-modal="true">
                                <button
                                    type="button"
                                    className="kav-simple-modal__backdrop"
                                    onClick={closePhoneCountrySelector}
                                    aria-label="Sluiten"
                                />
                                <div className="kav-simple-modal__card kav-country-modal">
                                    <h3>Kies landcode</h3>
                                    <div className="kav-country-modal__searchWrap">
                                        <i className="bi bi-search" aria-hidden="true"></i>
                                        <input
                                            className="kav-country-modal__search"
                                            type="text"
                                            value={phoneCountrySelectorState.searchTerm}
                                            onChange={onPhoneCountrySearchChange}
                                            placeholder="Zoek op land of code"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="kav-country-modal__list" role="listbox" aria-label="Landcodes">
                                        {filteredPhoneCountryOptions.length ? (
                                            filteredPhoneCountryOptions.map((country) => {
                                                const isSelected = country.countryCode === selectedPhoneCountry?.countryCode;

                                                return (
                                                    <button
                                                        key={`${country.countryCode}-${country.countryCallingCode}`}
                                                        type="button"
                                                        className={`kav-country-modal__item ${isSelected ? "is-selected" : ""}`}
                                                        onClick={() => selectPhoneCountry(country)}
                                                    >
                                                        <span className="kav-country-modal__itemFlag" aria-hidden="true">{country.flag}</span>
                                                        <span className="kav-country-modal__itemName">{country.countryNameEn}</span>
                                                        <span className="kav-country-modal__itemCode">+{country.countryCallingCode}</span>
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            <p className="kav-country-modal__empty">Geen landen gevonden.</p>
                                        )}
                                    </div>
                                    <div className="kav-auth-actions">
                                        <button
                                            type="button"
                                            className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                            onClick={closePhoneCountrySelector}
                                        >
                                            Annuleren
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <ReservationEmailField
                            value={accountData.email}
                            onChange={(event) =>
                                setAccountData((prev) => ({
                                    ...prev,
                                    email: event.target.value,
                                }))
                            }
                        />

                        <label className="kav-register-mobile__check">
                            <input
                                type="checkbox"
                                checked={accountData.sendPasswordBySms}
                                onChange={(event) => {
                                    const shouldSendBySms = event.target.checked;
                                    setAccountData((prev) => ({
                                        ...prev,
                                        sendPasswordBySms: shouldSendBySms,
                                    }));
                                    if (!shouldSendBySms) {
                                        setRequiresManualPhoneInput(false);
                                        return;
                                    }
                                    if (isPhoneAutoGenerated) {
                                        setPhoneInputData((prev) => ({
                                            ...prev,
                                            nationalNumber: "",
                                        }));
                                        setIsPhoneAutoGenerated(false);
                                        setRequiresManualPhoneInput(true);
                                    }
                                }}
                            />
                            <span>Stel een wachtwoord voor 30 dagen in en stuur het per sms naar mij.</span>
                        </label>

                        {
                            !accountData.sendPasswordBySms && (
                                <>
                                    <PasswordField
                                        label="Wachtwoord"
                                        name="register-password"
                                        value={accountData.password}
                                        showVisibilityToggle
                                        onChange={(event) =>
                                            setAccountData((prev) => ({
                                                ...prev,
                                                password: event.target.value,
                                            }))
                                        }
                                    />
                                    <div className="kav-register-mobile__hint">Minimaal 8 tekens en minimaal 1 cijfer en 1 letter</div>
                                    <PasswordField
                                        label="Herhaal wachtwoord"
                                        name="register-repeat-password"
                                        value={accountData.repeatPassword}
                                        showVisibilityToggle
                                        onChange={(event) =>
                                            setAccountData((prev) => ({
                                                ...prev,
                                                repeatPassword: event.target.value,
                                            }))
                                        }
                                    />
                                    {passwordMatchState !== "idle" && (
                                        <div
                                            className={`kav-register-mobile__passwordMatchHint ${passwordMatchState === "match" ? "is-match" : "is-mismatch"}`}
                                            role="status"
                                            aria-live="polite"
                                        >
                                            <i className={`bi ${passwordMatchState === "match" ? "bi-check-circle-fill" : "bi-x-circle-fill"}`} aria-hidden="true"></i>
                                            <span>
                                                {passwordMatchState === "match"
                                                    ? "Wachtwoorden komen overeen."
                                                    : "Wachtwoorden komen niet overeen."}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )
                        }

                        <div className="kav-auth-actions kav-register-mobile__actions">
                            <button
                                type="button"
                                className={`kav-step-actions__btn kav-step-actions__btn--primary ${!accountValid || !subsidiaryId ? "is-blocked" : ""}`}
                                onClick={onAccountContinue}
                            >
                                Doorgaan
                            </button>
                        </div>

                        {showAccountConfirmModal && (
                            <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setShowAccountConfirmModal(false)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <h3>Controleer je gegevens</h3>
                        <p>E-mailadres: <strong>{accountData.email}</strong></p>
                        <p>Telefoon: <strong>{accountData.phoneNumber}</strong></p>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => setShowAccountConfirmModal(false)}
                                disabled={isAccountCheckLoading}
                            >
                                Nee
                            </button>
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                onClick={onAccountConfirmYes}
                                disabled={isAccountCheckLoading}
                            >
                                {isAccountCheckLoading ? "Controleren..." : "Ja"}
                            </button>
                        </div>
                    </div>
                            </div>
                        )}

                        {showCustomerExistsModal && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setShowCustomerExistsModal(false)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <h3>Account bestaat al</h3>
                        <p>Er bestaat al een account met <strong>{accountData.email}</strong>.</p>
                        <p>Je kunt inloggen om verder te gaan met je reservering.</p>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => setShowCustomerExistsModal(false)}
                            >
                                Sluiten
                            </button>
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                onClick={onLoginInstead}
                            >
                                Terug naar inloggen
                            </button>
                        </div>
                    </div>
                </div>
                        )}

                        {showAnotherSubsidiaryModal && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setShowAnotherSubsidiaryModal(false)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <h3>Account al bekend</h3>
                        <p>Je account bestaat al bij een andere verhuurder. Neem contact op met support.</p>
                        <p><a href="tel:+31880883434">+31 88 088 3434</a></p>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => setShowAnotherSubsidiaryModal(false)}
                            >
                                Sluiten
                            </button>
                        </div>
                    </div>
                </div>
                        )}

            {showEmailNotValidModal && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => {
                            setShowEmailNotValidModal(false);
                            setIsEmailValidationUnavailable(false);
                        }}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <h3>{isEmailValidationUnavailable ? "E-mailcontrole is tijdelijk niet beschikbaar" : "E-mailadres is ongeldig"}</h3>
                        <p>
                            {
                                isEmailValidationUnavailable
                                    ? "We konden je e-mailadres nu niet controleren. Als je zeker weet dat het adres klopt, kun je doorgaan."
                                    : "Controleer je e-mailadres en probeer het opnieuw."
                            }
                        </p>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => {
                                    setShowEmailNotValidModal(false);
                                    setIsEmailValidationUnavailable(false);
                                    setStep(STEP_DOCUMENT_INTRO);
                                }}
                            >
                                Ik bevestig dat dit e-mailadres geldig is
                            </button>
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                onClick={() => {
                                    setShowEmailNotValidModal(false);
                                    setIsEmailValidationUnavailable(false);
                                }}
                            >
                                Sluiten
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderDocumentIntroStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Documentverificatie</h2>
            <div className="kav-register-mobile__illustration">
                <i className="bi bi-person-vcard"></i>
            </div>
            <ol className="kav-register-mobile__list">
                {!isMobileFlow && <li>Scan de QR-code met je telefoon.</li>}
                <li>Maak een foto van je rijbewijs.</li>
                <li>Maak een selfie.</li>
                <li>Controleer je gegevens{isMobileFlow ? "" : " op deze computer"}.</li>
            </ol>
            <div className="kav-auth-actions kav-register-mobile__actions">
                <button
                    type="button"
                    className="kav-step-actions__btn kav-step-actions__btn--primary"
                    onClick={() => setStep(STEP_DOCUMENT_CAPTURE)}
                >
                    Doorgaan
                </button>
            </div>
        </div>
    );

    const renderDocumentCaptureStep = () => (
        <DocumentCapturePanel
            isMobileFlow={isMobileFlow}
            phase={documentCapture.phase}
            error={documentCapture.error}
            pollWarning={documentCapture.pollWarning}
            session={documentCapture.session}
            sessionStatus={documentCapture.sessionStatus}
            onRetry={onDocumentCaptureRetry}
            onCaptureMessage={onDocumentCaptureMessage}
            onBack={onDocumentCaptureBack}
            onFrameLoad={pushHistoryGuard}
        />
    );

    const renderDocumentCountryField = ({field, id}) => {
        const fieldMeta = DOCUMENT_COUNTRY_FIELD_META[field];
        if (!fieldMeta) return null;

        const selectedOption = getCountryOptionByAlpha2(documentData[field]);
        const fallbackCode = String(documentData[field] || "").trim().toUpperCase();
        const isOpened = countrySelectorState.isOpen && countrySelectorState.field === field;

        return (
            <div className="kav-input-field">
                <label className="kav-input-field__label" htmlFor={id}>{fieldMeta.label}</label>
                <button
                    id={id}
                    type="button"
                    className="kav-country-select"
                    onClick={() => openCountrySelector(field)}
                    aria-haspopup="dialog"
                    aria-expanded={isOpened}
                >
                    <span className="kav-country-select__value">
                        {selectedOption ? (
                            <>
                                <span className="kav-country-select__flag" aria-hidden="true">{selectedOption.flag}</span>
                                <span className="kav-country-select__name">{selectedOption.countryNameEn}</span>
                                <span className="kav-country-select__code">{selectedOption.countryCode}</span>
                            </>
                        ) : fallbackCode ? (
                            <span className="kav-country-select__code">{fallbackCode}</span>
                        ) : (
                            <span className="kav-country-select__placeholder">Selecteer land</span>
                        )}
                    </span>
                    <i className="bi bi-chevron-down" aria-hidden="true"></i>
                </button>
            </div>
        );
    };

    const renderDocumentDataStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Documentverificatie</h2>
            <div className="kav-register-mobile__status">
                <span className="kav-register-mobile__statusItem is-success">
                    <i className="bi bi-camera-fill"></i> Rijbewijs
                </span>
                <span className={`kav-register-mobile__statusItem ${hasSelfiePhoto ? "is-success" : "is-pending"}`}>
                    <i className="bi bi-camera-fill"></i> Selfie
                </span>
            </div>

            {documentOcrError && (
                <div className="kav-callout kav-auth-error">
                    {documentOcrError}
                </div>
            )}

            <div className="kav-input-field">
                <label className="kav-input-field__label" htmlFor="doc-name">Voornaam</label>
                <input
                    id="doc-name"
                    className="kav-input-field__input"
                    value={documentData.firstName}
                    onChange={(event) => setDocumentData((prev) => ({...prev, firstName: event.target.value}))}
                />
            </div>
            <div className="kav-input-field">
                <label className="kav-input-field__label" htmlFor="doc-surname">Achternaam</label>
                <input
                    id="doc-surname"
                    className="kav-input-field__input"
                    value={documentData.surname}
                    onChange={(event) => setDocumentData((prev) => ({...prev, surname: event.target.value}))}
                />
            </div>
            {renderDocumentCountryField({field: "issueCountry", id: "doc-country"})}
            <div className="kav-register-mobile__grid2">
                <div className="kav-input-field">
                    <label className="kav-input-field__label" htmlFor="doc-license">Rijbewijsnummer</label>
                    <input
                        id="doc-license"
                        className="kav-input-field__input"
                        value={documentData.licenseNumber}
                        onChange={(event) => setDocumentData((prev) => ({...prev, licenseNumber: event.target.value}))}
                    />
                </div>
                <div className="kav-input-field">
                    <label className="kav-input-field__label" htmlFor="doc-expiry">Vervaldatum</label>
                    <input
                        id="doc-expiry"
                        className="kav-input-field__input"
                        type="date"
                        value={formatDocumentDateForInput(documentData.expirationDate)}
                        onChange={(event) => setDocumentData((prev) => ({...prev, expirationDate: event.target.value}))}
                    />
                </div>
            </div>
            <div className="kav-register-mobile__grid2">
                <div className="kav-input-field">
                    <label className="kav-input-field__label" htmlFor="doc-birth">Geboortedatum</label>
                    <input
                        id="doc-birth"
                        className="kav-input-field__input"
                        type="date"
                        value={formatDocumentDateForInput(documentData.birthDate)}
                        onChange={(event) => setDocumentData((prev) => ({...prev, birthDate: event.target.value}))}
                    />
                </div>
                <label className="kav-register-mobile__switch">
                    <span>Categorie B</span>
                    <input
                        type="checkbox"
                        checked={documentData.categoryB}
                        onChange={(event) => setDocumentData((prev) => ({...prev, categoryB: event.target.checked}))}
                    />
                </label>
            </div>
            {renderDocumentCountryField({field: "birthCountry", id: "doc-birth-country"})}
            {renderDocumentCountryField({field: "nationality", id: "doc-nationality"})}

            <div className="kav-auth-actions kav-register-mobile__actions">
                <button
                    type="button"
                    className={`kav-step-actions__btn kav-step-actions__btn--primary ${!canContinueDocumentData ? "is-blocked" : ""}`}
                    onClick={onDocumentDataContinue}
                >
                    Doorgaan
                </button>
            </div>

            {countrySelectorState.isOpen && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={closeCountrySelector}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card kav-country-modal">
                        <h3>{activeCountryFieldMeta?.modalTitle || "Kies een land"}</h3>
                        <div className="kav-country-modal__searchWrap">
                            <i className="bi bi-search" aria-hidden="true"></i>
                            <input
                                className="kav-country-modal__search"
                                type="text"
                                value={countrySelectorState.searchTerm}
                                onChange={onCountrySearchChange}
                                placeholder="Zoek op land of code"
                                autoFocus
                            />
                        </div>
                        <div className="kav-country-modal__list" role="listbox" aria-label="Landenlijst">
                            {filteredCountryOptions.length ? (
                                filteredCountryOptions.map((country) => {
                                    const selectedAlpha2 = normalizeCountryAlpha2(documentData[countrySelectorState.field]);
                                    const isSelected = selectedAlpha2 === country.countryCode;

                                    return (
                                        <button
                                            key={country.countryCode}
                                            type="button"
                                            className={`kav-country-modal__item ${isSelected ? "is-selected" : ""}`}
                                            onClick={() => selectCountryForActiveField(country.countryCode)}
                                        >
                                            <span className="kav-country-modal__itemFlag" aria-hidden="true">{country.flag}</span>
                                            <span className="kav-country-modal__itemName">{country.countryNameEn}</span>
                                            <span className="kav-country-modal__itemCode">{country.countryCode}</span>
                                        </button>
                                    );
                                })
                            ) : (
                                <p className="kav-country-modal__empty">Geen landen gevonden.</p>
                            )}
                        </div>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={closeCountrySelector}
                            >
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDocumentConfirmModal && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={() => setShowDocumentConfirmModal(false)}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card">
                        <h3>Controleer je gegevens</h3>
                        <p>
                            Controleer alle ingevulde gegevens goed. Bij foutieve gegevens kan je rijbewijs niet worden goedgekeurd.
                        </p>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={() => setShowDocumentConfirmModal(false)}
                            >
                                Controleren
                            </button>
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--primary"
                                onClick={() => {
                                    setShowDocumentConfirmModal(false);
                                    setStep(hasSelfiePhoto ? STEP_ADDRESS : STEP_SELFIE);
                                }}
                            >
                                Doorgaan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderSelfieStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Selfie</h2>
            {
                isLivenessActive ? (
                    <LivenessDetector
                        key={livenessDetectorKey}
                        backendWebSocketUrl={LIVENESS_WS_URL}
                        texts={livenessTexts}
                        onComplete={onLivenessComplete}
                        onProgress={onLivenessProgress}
                        onError={onLivenessError}
                    />
                ) : (
                    <div className="kav-register-mobile__illustration">
                        {
                            selfiePhoto
                                ? <img src={selfiePhoto} alt="Selfie" style={{maxWidth: "100%", maxHeight: 220, borderRadius: 10}} />
                                : <i className="bi bi-camera2"></i>
                        }
                    </div>
                )
            }
            <h3 className="kav-h3">Let op</h3>
            <p className="kav-register-mobile__text">
                Volg de liveness-instructies. Maak geen foto van een foto of van een ander scherm.
            </p>
            {!hasSelfiePhoto && isLivenessActive && canUseFallbackSelfieCapture ? (
                <div className="kav-auth-actions kav-register-mobile__actions">
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                        onClick={openFallbackSelfieCapture}
                    >
                        Gewone selfie maken
                    </button>
                </div>
            ) : null}
            <label className="kav-register-mobile__check kav-register-mobile__check--subtle">
                <input
                    className="kav-register-mobile__checkInput--subtle"
                    type="checkbox"
                    checked={agreedSelfieStorage}
                    onChange={(event) => setAgreedSelfieStorage(event.target.checked)}
                />
                <span className="kav-register-mobile__checkText--subtle">Ik ga akkoord met het opslaan van mijn selfie in het KAV2GO-platform.</span>
            </label>

            {!hasSelfiePhoto && livenessProgress.message && (
                <div className="kav-callout kav-auth-status">
                    {livenessProgress.message}
                </div>
            )}

            {isSelfieCaptureProcessing && (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Selfie wordt verwerkt...</span>
                </div>
            )}

            {isFaceIdLoading && (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Face ID controle wordt uitgevoerd...</span>
                </div>
            )}

            {livenessError && (
                <div className="kav-callout kav-auth-error">
                    {livenessError}
                </div>
            )}

            {registrationError && (
                <div className="kav-callout kav-auth-error">
                    {registrationError}
                </div>
            )}

            <div className="kav-auth-actions kav-register-mobile__actions">
                {!hasSelfiePhoto && !isLivenessActive && !isFaceIdLoading && !isSelfieCaptureProcessing ? (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                        onClick={startLivenessDetection}
                    >
                        Start liveness-check
                    </button>
                ) : null}

                {!hasSelfiePhoto && isLivenessActive ? (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                        onClick={() => setIsLivenessActive(false)}
                    >
                        Annuleren
                    </button>
                ) : null}

                {hasSelfiePhoto ? (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                        onClick={startLivenessDetection}
                        disabled={isFaceIdLoading || isSelfieCaptureProcessing}
                    >
                        Opnieuw
                    </button>
                ) : null}

                {hasSelfiePhoto ? (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                        onClick={() => setStep(STEP_ADDRESS)}
                        disabled={isFaceIdLoading || isSelfieCaptureProcessing}
                    >
                        Doorgaan
                    </button>
                ) : null}
            </div>
        </div>
    );

    const renderAddressFields = (type, title) => {
        const block = addressData[type];
        const isLoading = addressLookupLoading[type];
        const selectedCountryOption = getCountryOptionByAlpha2(block.country);
        const fallbackCountryCode = String(block.country || "").trim().toUpperCase();
        const isCountryModalOpen = addressCountrySelectorState.isOpen && addressCountrySelectorState.addressType === type;

        return (
            <div className="kav-register-mobile__addressBlock">
                <h3 className="kav-h3">{title}</h3>
                <div className="kav-register-mobile__grid2">
                    <div className="kav-input-field">
                        <label className="kav-input-field__label" htmlFor={`${type}-zip`}>Postcode</label>
                        <input
                            id={`${type}-zip`}
                            className="kav-input-field__input"
                            value={block.zip_code}
                            onChange={(event) => setAddressField(type, "zip_code", normalizeZipCode(event.target.value))}
                            onBlur={() => lookupAddressByPostalCode({
                                type,
                                zipCode: block.zip_code,
                                houseNumber: block.house_number,
                                country: block.country,
                            })}
                            placeholder="1234AB"
                        />
                    </div>
                    <div className="kav-input-field">
                        <label className="kav-input-field__label" htmlFor={`${type}-house`}>Huisnummer</label>
                        <input
                            id={`${type}-house`}
                            className="kav-input-field__input"
                            value={block.house_number}
                            onChange={(event) => setAddressField(type, "house_number", event.target.value.replace(/[^\d]/g, ""))}
                            onBlur={() => lookupAddressByPostalCode({
                                type,
                                zipCode: block.zip_code,
                                houseNumber: block.house_number,
                                country: block.country,
                            })}
                            placeholder="10"
                        />
                    </div>
                </div>
                <div className="kav-input-field">
                    <label className="kav-input-field__label" htmlFor={`${type}-addition`}>Toevoeging</label>
                    <input
                        id={`${type}-addition`}
                        className="kav-input-field__input"
                        value={block.house_number_extension}
                        onChange={(event) => setAddressField(type, "house_number_extension", event.target.value)}
                        placeholder="A"
                    />
                </div>
                <div className="kav-input-field">
                    <label className="kav-input-field__label" htmlFor={`${type}-street`}>Straat</label>
                    <input
                        id={`${type}-street`}
                        className="kav-input-field__input"
                        value={block.street_name}
                        onChange={(event) => setAddressField(type, "street_name", event.target.value)}
                        placeholder="Straatnaam"
                        disabled={isLoading}
                    />
                </div>
                <div className="kav-register-mobile__grid2">
                    <div className="kav-input-field">
                        <label className="kav-input-field__label" htmlFor={`${type}-city`}>Plaats</label>
                        <input
                            id={`${type}-city`}
                            className="kav-input-field__input"
                            value={block.city}
                            onChange={(event) => setAddressField(type, "city", event.target.value)}
                            placeholder="Amsterdam"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="kav-input-field">
                        <label className="kav-input-field__label" htmlFor={`${type}-country`}>Land</label>
                        <button
                            id={`${type}-country`}
                            type="button"
                            className="kav-country-select"
                            onClick={() => openAddressCountrySelector(type)}
                            aria-haspopup="dialog"
                            aria-expanded={isCountryModalOpen}
                        >
                            <span className="kav-country-select__value">
                                {selectedCountryOption ? (
                                    <>
                                        <span className="kav-country-select__flag" aria-hidden="true">{selectedCountryOption.flag}</span>
                                        <span className="kav-country-select__name">{selectedCountryOption.countryNameEn}</span>
                                        <span className="kav-country-select__code">{selectedCountryOption.countryCode}</span>
                                    </>
                                ) : fallbackCountryCode ? (
                                    <span className="kav-country-select__code">{fallbackCountryCode}</span>
                                ) : (
                                    <span className="kav-country-select__placeholder">Selecteer land</span>
                                )}
                            </span>
                            <i className="bi bi-chevron-down" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderAddressStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Adresgegevens</h2>
            <p className="kav-register-mobile__text">
                Vul je woonadres in. Op basis van postcode en huisnummer vullen we straat en plaats automatisch aan.
            </p>

            {(addressLookupLoading.residence || addressLookupLoading.invoice) && (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Adresgegevens worden opgehaald...</span>
                </div>
            )}

            {isRegistrationLoading && (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Registratie wordt afgerond. Dit kan een paar seconden duren...</span>
                </div>
            )}

            {addressLookupError && (
                <div className="kav-callout kav-auth-error">
                    {addressLookupError}
                </div>
            )}

            {addressValidationError && (
                <div className="kav-callout kav-auth-error">
                    {addressValidationError}
                </div>
            )}

            {registrationError && (
                <div className="kav-callout kav-auth-error">
                    {registrationError}
                </div>
            )}

            {renderAddressFields("residence", "Woonadres")}

            <label className="kav-register-mobile__check">
                <input
                    type="checkbox"
                    checked={addressSameAsResidenceAddress}
                    onChange={(event) => setAddressSameAsResidenceAddress(event.target.checked)}
                />
                <span>Gebruik hetzelfde adres voor de factuur</span>
            </label>

            {!addressSameAsResidenceAddress && renderAddressFields("invoice", "Factuuradres")}

            {addressCountrySelectorState.isOpen && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-simple-modal__backdrop"
                        onClick={closeAddressCountrySelector}
                        aria-label="Sluiten"
                    />
                    <div className="kav-simple-modal__card kav-country-modal">
                        <h3>Kies land</h3>
                        <div className="kav-country-modal__searchWrap">
                            <i className="bi bi-search" aria-hidden="true"></i>
                            <input
                                className="kav-country-modal__search"
                                type="text"
                                value={addressCountrySelectorState.searchTerm}
                                onChange={onAddressCountrySearchChange}
                                placeholder="Zoek op land of code"
                                autoFocus
                            />
                        </div>
                        <div className="kav-country-modal__list" role="listbox" aria-label="Landenlijst">
                            {filteredAddressCountryOptions.length ? (
                                filteredAddressCountryOptions.map((country) => {
                                    const selectedAddressType = addressCountrySelectorState.addressType;
                                    const selectedAlpha2 = normalizeCountryAlpha2(addressData[selectedAddressType]?.country);
                                    const isSelected = selectedAlpha2 === country.countryCode;

                                    return (
                                        <button
                                            key={country.countryCode}
                                            type="button"
                                            className={`kav-country-modal__item ${isSelected ? "is-selected" : ""}`}
                                            onClick={() => selectAddressCountry(country.countryCode)}
                                        >
                                            <span className="kav-country-modal__itemFlag" aria-hidden="true">{country.flag}</span>
                                            <span className="kav-country-modal__itemName">{country.countryNameEn}</span>
                                            <span className="kav-country-modal__itemCode">{country.countryCode}</span>
                                        </button>
                                    );
                                })
                            ) : (
                                <p className="kav-country-modal__empty">Geen landen gevonden.</p>
                            )}
                        </div>
                        <div className="kav-auth-actions">
                            <button
                                type="button"
                                className="kav-step-actions__btn kav-step-actions__btn--ghost"
                                onClick={closeAddressCountrySelector}
                            >
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="kav-auth-actions kav-register-mobile__actions">
                <button
                    type="button"
                    className="kav-step-actions__btn kav-step-actions__btn--ghost"
                    onClick={() => setStep(STEP_SELFIE)}
                    disabled={isRegistrationLoading}
                >
                    Terug
                </button>
                <button
                    type="button"
                    className={`kav-step-actions__btn kav-step-actions__btn--primary ${!addressValid ? "is-blocked" : ""}`}
                    onClick={onAddressContinue}
                    disabled={isRegistrationLoading}
                >
                    {isRegistrationLoading && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                    {isRegistrationLoading ? "Registreren..." : "Doorgaan"}
                </button>
            </div>
        </div>
    );

    const renderContractStep = () => (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Contract ondertekenen</h2>
            <p className="kav-register-mobile__text">
                Lees de documenten en ga akkoord om je registratie af te ronden.
            </p>

            {contractError && (
                <div className="kav-callout kav-auth-error">
                    {contractError}
                </div>
            )}

            {isContractFilesLoading && (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Contractbestanden worden geladen...</span>
                </div>
            )}

            {isContractSigning && (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Contract wordt ondertekend...</span>
                </div>
            )}

            <div className="kav-register-mobile__contractList">
                {!isContractFilesLoading && contractFiles.length === 0 && (
                    <div className="kav-callout">
                        Er zijn geen aanvullende contractdocumenten gevonden. Je kunt direct verder.
                    </div>
                )}
                {contractFiles.map((contractFile) => (
                    <div key={contractFile.type} className="kav-register-mobile__contractItem">
                        <label className="kav-register-mobile__check">
                            <input
                                type="checkbox"
                                checked={contractFile.checked}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    setContractFiles((prev) => prev.map((item) => (
                                        item.type === contractFile.type
                                            ? {...item, checked}
                                            : item
                                    )));
                                }}
                            />
                            <span>
                                Ik heb {contractFile.type === "contract" ? "het klantcontract" : "dit document"} gelezen en ga akkoord.
                            </span>
                        </label>
                        <a href={contractFile.url} target="_blank" rel="noreferrer">
                            Open document
                        </a>
                    </div>
                ))}
            </div>

            <div className="kav-auth-actions kav-register-mobile__actions">
                <button
                    type="button"
                    className="kav-step-actions__btn kav-step-actions__btn--ghost"
                    onClick={() => setStep(STEP_ADDRESS)}
                    disabled={isContractSigning}
                >
                    Terug
                </button>
                <button
                    type="button"
                    className={`kav-step-actions__btn kav-step-actions__btn--primary ${!allContractsAccepted ? "is-blocked" : ""}`}
                    onClick={onSignContract}
                    disabled={isContractSigning || isContractFilesLoading}
                >
                    {isContractSigning && <span className="kav-spinner kav-spinner--light" aria-hidden="true"></span>}
                    {isContractSigning ? "Ondertekenen..." : "Verder met reservering"}
                </button>
            </div>
        </div>
    );

    const renderStep = () => {
        const shouldShowDemoRegistrationSuccess = isDemoReservationLocation && isDemoRegistrationCompleted;

        if (shouldShowDemoRegistrationSuccess) {
            return renderDemoRegistrationSuccess();
        }

        if (isVehicleLoading) {
            return (
                <div className="kav-panel kav-register-mobile">
                    <div className="kav-callout kav-auth-status">
                        <span className="kav-spinner" aria-hidden="true"></span>
                        <span>Gegevens worden geladen...</span>
                    </div>
                </div>
            );
        }

        if (vehicleLoadError) {
            return (
                <div className="kav-panel kav-register-mobile">
                    <div className="kav-callout kav-auth-error">{vehicleLoadError}</div>
                    <div className="kav-auth-actions" style={{marginTop: 14}}>
                        <NavLink
                            to={reservationConfirmLink}
                            className="kav-step-actions__btn kav-step-actions__btn--ghost"
                        >
                            Terug naar reservering
                        </NavLink>
                    </div>
                </div>
            );
        }

        if (step === STEP_ACCESS) return renderAccessStep();
        if (step === STEP_ACCOUNT) return renderAccountStep();
        if (step === STEP_DOCUMENT_INTRO) return renderDocumentIntroStep();
        if (step === STEP_DOCUMENT_CAPTURE) return renderDocumentCaptureStep();
        if (step === STEP_DOCUMENT_DATA) return renderDocumentDataStep();
        if (step === STEP_SELFIE) return renderSelfieStep();
        if (step === STEP_ADDRESS) return renderAddressStep();
        if (step === STEP_CONTRACT) return renderContractStep();
        return renderAccessStep();
    };
    const isDemoDesktopSuccess = isDemoReservationLocation && !isMobileFlow && isDemoRegistrationCompleted;

    return (
        <>
            <Header/>
            <Devider />
            <input
                ref={nativeSelfieCaptureInputRef}
                type="file"
                accept="image/*"
                capture="user"
                style={{display: "none"}}
                onChange={onNativeSelfieCaptureFileChange}
            />

            <div className="container mt-4 mb-5">
                {
                    isDemoReservationLocation && !isMobileFlow ? (
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="kav-reservation-top kav-reservation-top--stackedLogo">
                                    <div className="kav-reservation-top__demoLogoWrap" aria-hidden="true">
                                        <img src={demoLogo} alt="" className="kav-reservation-top__demoLogo"/>
                                    </div>
                                    <div className="kav-demo-step-banner kav-demo-step-banner--flat">
                                        {
                                            isDemoRegistrationCompleted ? (
                                                <>
                                                    <span className="kav-demo-step-banner__badge">Stap 6</span>
                                                    <p className="kav-demo-step-banner__mixedText">
                                                        <span className="kav-demo-step-banner__strong">
                                                            Als de klant de checks goed heeft doorlopen is de registratie gelukt en kan de reservering worden afgerond.
                                                        </span>
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="kav-demo-step-banner__badge">Stap 5</span>
                                                    <p className="kav-demo-step-banner__mixedText">
                                                        Verificatie van document en persoon verloopt via de smartphone. Scan de QR code, maak de foto&apos;s en ga daarna op deze computer verder.
                                                    </p>
                                                </>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null
                }
                <div className="row justify-content-center">
                    <div className={isDemoDesktopSuccess ? "col-12" : "col-12 col-md-8 col-lg-6"}>
                        {renderStep()}
                    </div>
                </div>
            </div>

            {isDocumentCaptureResultProcessing && (
                <div className="kav-simple-modal" role="dialog" aria-modal="true" aria-label="Document wordt verwerkt">
                    <div className="kav-simple-modal__backdrop" aria-hidden="true" />
                    <div className="kav-simple-modal__card kav-loading-modal">
                        <div className="kav-loading-modal__spinnerWrap">
                            <span className="kav-spinner" aria-hidden="true"></span>
                        </div>
                        <h3>Rijbewijs wordt gecontroleerd</h3>
                        <p>We lezen de gegevens van je document uit. Dit kan enkele seconden duren.</p>
                    </div>
                </div>
            )}
            <Footer/>
        </>
    );
};

export default ReservationRegisterPage;
